import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { PlatformLogo } from "./PlatformLogo";
import { channelSpendComplete, roasPresentation } from "./lib/roas";
import {
  AlertCircle,
  ArrowUpRight,
  CalendarDays,
  Check,
  CircleDashed,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Database,
  FileSpreadsheet,
  Gauge,
  LockKeyhole,
  LogOut,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Trash2,
  Upload,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { type Centre, type DetailRow, type MonthRow, type SpendProofEntry } from "./data/mockData";
import { countLeads, trimLeadSourcesCsv, type LeadCountRow } from "./lib/sales-csv";
import {
  api,
  ApiClientError,
  type FxRate,
  type MasterEntry,
  type MasterEntryType,
  type ApprovedUser,
  type PlatformImportGroup,
  type FacebookAdNode,
  type Freshness,
  type FacebookAdsResponse,
  type GoogleSearchTermsResponse,
  type SalesImportPreview,
  type SalesImportStatus,
  type PlatformImportPreview,
  type ComposioStatus,
  type RevenueProof,
  type LeadCounts,
  type FeedPayment,
  type McpConnectionRequest,
} from "./lib/api";

type View = "report" | "cpl" | "plan" | "master" | "data" | "mcp";

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const money2 = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactMoney = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

const planningMoney = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

const currentMonthKey = (() => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  return year && month ? `${year}-${month}` : new Date().toISOString().slice(0, 7);
})();

const currentDateKey = (() => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : new Date().toISOString().slice(0, 10);
})();

const lastDateOfMonth = (month: string): string => {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10);
};

function Brand({ inverse = false }: { inverse?: boolean }) {
  return (
    <div className={`brand ${inverse ? "brand--inverse" : ""}`} aria-label="DentSpa Advertising">
      <img src={inverse ? "/logo-white.svg" : "/logo-light.svg"} alt="DentSpa" width="113" height="22" />
      <span className="brand__product">Advertising</span>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="19" height="19">
      <path fill="#4285F4" d="M21.35 12.25c0-.71-.06-1.22-.2-1.75H12v3.42h5.37a4.73 4.73 0 0 1-2 3.04l-.02.11 2.91 2.25.2.02c1.86-1.71 2.89-4.23 2.89-7.09Z" />
      <path fill="#34A853" d="M12 21.75c2.62 0 4.82-.86 6.43-2.41l-3.09-2.38c-.83.57-1.94.96-3.34.96a5.8 5.8 0 0 1-5.49-4l-.11.01-3.03 2.34-.04.11A9.71 9.71 0 0 0 12 21.75Z" />
      <path fill="#FBBC05" d="M6.51 13.92A5.98 5.98 0 0 1 6.2 12c0-.67.12-1.32.3-1.92l-.01-.13-3.07-2.38-.1.05A9.76 9.76 0 0 0 2.25 12c0 1.58.38 3.08 1.08 4.38l3.18-2.46Z" />
      <path fill="#EA4335" d="M12 6.08c1.83 0 3.06.79 3.76 1.43l2.73-2.67C16.82 3.28 14.62 2.25 12 2.25a9.71 9.71 0 0 0-8.67 5.37l3.17 2.46A5.82 5.82 0 0 1 12 6.08Z" />
    </svg>
  );
}

function LoginScreen() {
  return (
    <main className="login-page">
      <section className="login-intro">
        <Brand inverse />
        <div className="login-intro__copy">
          <p className="eyebrow eyebrow--light">Advertising intelligence</p>
          <h1>A clear view of every advertising dollar.</h1>
          <p>Plan spend, monitor actuals, and follow the return of each monthly lead cohort as it matures.</p>
        </div>
        <div className="login-intro__footer">
          <span>Two call centres</span>
          <span aria-hidden="true">•</span>
          <span>One reporting truth</span>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <div className="login-card__heading">
            <div className="login-card__seal"><ShieldCheck size={22} /></div>
            <h2>Sign in to DentSpa</h2>
            <p>Use an approved Google Workspace account to continue.</p>
          </div>
          <button className="google-button" type="button" onClick={() => window.location.assign("/api/auth/google/start")}>
            <GoogleIcon />
            Continue with Google
          </button>
          <div className="login-rule"><span>Access is restricted</span></div>
          <div className="login-security">
            <LockKeyhole size={17} />
            <p>Only Gmail addresses approved by an administrator can access reporting data.</p>
          </div>
        </div>
        <p className="login-help">Need access? Contact a DentSpa reporting administrator.</p>
      </section>
    </main>
  );
}

function MetricCard({ label, value, note, tone = "neutral", icon, onValueClick, valueTitle }: {
  label: string;
  value: string;
  note: string;
  tone?: "neutral" | "positive" | "warning";
  icon: ReactNode;
  onValueClick?: () => void;
  valueTitle?: string;
}) {
  return (
    <article className="metric-card">
      <div className="metric-card__top">
        <span>{label}</span>
        <span className="metric-card__icon">{icon}</span>
      </div>
      {onValueClick ? <button type="button" className="metric-card__value-button" title={valueTitle} onClick={onValueClick}><strong>{value}</strong></button> : <strong>{value}</strong>}
      <p className={`metric-card__note metric-card__note--${tone}`}>{note}</p>
    </article>
  );
}

type ChannelSummary = {
  spendComplete?: boolean;
  id: "meta" | "google" | "tiktok" | "microsoft" | "x" | "pinterest";
  label: string;
  spend: number;
  revenue: number;
  sales: number;
};

function ChannelMetricCard({ channel, onViewRevenue, onViewSpend }: { channel: ChannelSummary; onViewRevenue?: () => void; onViewSpend?: () => void }) {
  const roas = roasPresentation(channel.spend, channel.revenue, channel.spendComplete);
  return (
    <article className="channel-metric-card">
      <div className="channel-metric-card__top">
        <span className="channel-metric-card__label"><i className={`platform-mark platform-mark--${channel.id}`} />{channel.label}</span>
        <span>YTD</span>
      </div>
      <div title={roas.provisional ? 'Revenue divided by recorded spend for this channel. Pending spend verification may change this result.' : undefined} className={`channel-metric-card__roas ${roas.provisional ? "" : roas.positive ? "channel-metric-card__roas--positive" : "channel-metric-card__roas--negative"}`}>
        <strong>{roas.value}</strong>
        <span>{roas.label}</span>
      </div>
      <div className="channel-metric-card__values channel-metric-card__values--three">
        <div title={money.format(channel.spend)}><span>Spent</span>{onViewSpend
          ? <button type="button" className="channel-metric-card__proof" title="Every spend entry behind this number, with its source and conversion" onClick={onViewSpend}><strong>{compactMoney.format(channel.spend)}</strong></button>
          : <strong>{compactMoney.format(channel.spend)}</strong>}</div>
        <div title={money.format(channel.revenue)}><span>Revenue</span>{onViewRevenue
          ? <button type="button" className="channel-metric-card__proof" title="Every payment behind this number, with its lead source and attribution" onClick={onViewRevenue}><strong>{compactMoney.format(channel.revenue)}</strong></button>
          : <strong>{compactMoney.format(channel.revenue)}</strong>}</div>
        <div title={channel.sales ? money.format(channel.revenue / channel.sales) : undefined}><span>Cart value</span><strong>{channel.sales > 0 ? compactMoney.format(channel.revenue / channel.sales) : "—"}</strong></div>
      </div>
    </article>
  );
}

const shortDate = (iso?: string): string | null => {
  if (!iso) return null;
  const parsed = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const freshnessStatus = (iso?: string | null): "fresh" | "stale" | "missing" => {
  if (!iso) return "missing";
  const stamp = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(stamp.getTime())) return "missing";
  return Date.now() - stamp.getTime() > 14 * 86_400_000 ? "stale" : "fresh";
};

const freshnessAge = (iso: string): string => {
  const stamp = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(stamp.getTime())) return "recently";
  const hours = (Date.now() - stamp.getTime()) / 3_600_000;
  if (hours < 1) return "just now";
  if (hours < 24) return "today";
  if (hours < 48) return "yesterday";
  return stamp.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

function SourceBadge({ source, updatedAt, onClick }: { source: DetailRow["source"]; updatedAt?: string; onClick?: () => void }) {
  const icon = source === "Synced" ? <RefreshCw size={12} /> : source === "Uploaded" ? <Upload size={12} /> : <CircleDollarSign size={12} />;
  const freshness = shortDate(updatedAt);
  const content = <>{icon}{source}{freshness && <em className="source-badge__date">{freshness}</em>}{onClick && <ArrowUpRight size={10} />}</>;
  return onClick
    ? <button type="button" className={`source-badge source-badge--button source-badge--${source.toLowerCase()}`} onClick={onClick} aria-label={`View ${source.toLowerCase()} spend source details`}>{content}</button>
    : <span className={`source-badge source-badge--${source.toLowerCase()}`}>{content}</span>;
}

const CENTRE_FLAGS: Record<Centre, string> = { Istanbul: "🇹🇷", Cairo: "🇪🇬", Unallocated: "—" };

function CentreBadge({ centre }: { centre: Centre }) {
  return <span className={`centre-badge centre-badge--${centre.toLowerCase()} centre-badge--flag`} role="img" aria-label={centre} title={centre}>{CENTRE_FLAGS[centre]}</span>;
}

const CHANNEL_ORDER = ["Meta", "Google", "TikTok", "Microsoft", "X Ads", "Pinterest"] as const;
type Channel = (typeof CHANNEL_ORDER)[number];

const channelOf = (detail: DetailRow): Channel => {
  const id = detail.platformId ?? detail.platform.toLowerCase();
  if (id.startsWith("meta")) return "Meta";
  if (id.startsWith("google")) return "Google";
  if (id.startsWith("tiktok")) return "TikTok";
  if (id.startsWith("microsoft")) return "Microsoft";
  if (id.startsWith("x")) return "X Ads";
  return "Pinterest";
};

// Collapses per-account platform rows into channel rows: Cairo, Istanbul,
// then Combined (both centres plus unallocated spend and sales).
const mergeDetails = (label: string, centre: Centre, rows: DetailRow[]): DetailRow => {
  const sourcePriority = (source: DetailRow["source"]): number => source === "Synced" ? 3 : source === "Uploaded" ? 2 : 1;
  const recorded = rows.filter(row => row.sourceEntries?.length);
  const best = [...(recorded.length ? recorded : rows)].sort((left, right) => sourcePriority(right.source) - sourcePriority(left.source))[0];
  return {
    platform: label,
    platformId: best.platformId,
    centre,
    currency: best.currency,
    plan: rows.reduce((sum, row) => sum + row.plan, 0),
    spend: rows.reduce((sum, row) => sum + row.spend, 0),
    spendComplete: rows.every(row => row.spendComplete !== false),
    sales: rows.reduce((sum, row) => sum + row.sales, 0),
    revenue: rows.reduce((sum, row) => sum + row.revenue, 0),
    estimatedSales: rows.reduce((sum, row) => sum + (row.estimatedSales ?? 0), 0),
    estimatedRevenue: rows.reduce((sum, row) => sum + (row.estimatedRevenue ?? 0), 0),
    source: new Set(rows.filter(row => row.sourceEntries?.length).map(row => row.source)).size > 1 ? "Mixed" : best.source,
    sourceCurrency: best.sourceCurrency,
    sourceCurrencies: [...new Set(rows.flatMap((row) => row.sourceCurrencies ?? (row.sourceCurrency ? [row.sourceCurrency] : [])))],
    sourceReferences: [...new Set(rows.flatMap((row) => row.sourceReferences ?? []))],
    accountIds: [...new Set(rows.flatMap((row) => row.accountIds ?? []))],
    sourceRate: best.sourceRate,
    sourceRates: [...new Set(rows.flatMap((row) => row.sourceRates ?? (row.sourceRate ? [row.sourceRate] : [])))],
    sourceLocked: rows.every((row) => row.sourceLocked !== false),
    sourceUpdatedAt: rows.map((row) => row.sourceUpdatedAt ?? "").sort().at(-1) || undefined,
    revenueUpdatedAt: rows.filter(row => row.revenueUpdatedAt).map(row => row.revenueUpdatedAt!).sort()[0],
    revenueSource: [...new Set(rows.filter(row => row.revenueUpdatedAt).map(row => row.revenueSource))].join(' + ') || 'Not recorded',
    sourceEntries: rows.flatMap((row) => row.sourceEntries ?? []),
  };
};

type ChannelRow = { detail: DetailRow; combined: boolean; centreLabel: string };

const channelRows = (details: DetailRow[]): ChannelRow[] => {
  const rows: ChannelRow[] = [];
  for (const channel of CHANNEL_ORDER) {
    const channelDetails = details.filter((detail) => channelOf(detail) === channel);
    if (!channelDetails.length) continue;
    const hasAny = channelDetails.some((detail) => detail.plan || detail.spend || detail.sales || detail.revenue);
    if (!hasAny) continue;
    const centres: Centre[] = ["Cairo", "Istanbul", "Unallocated"];
    const centreRows = centres
      .map((centre) => ({ centre, rows: channelDetails.filter((detail) => detail.centre === centre) }))
      .filter((entry) => entry.rows.length && entry.rows.some((row) => row.plan || row.spend || row.sales || row.revenue));
    for (const entry of centreRows) {
      rows.push({ detail: mergeDetails(channel, entry.centre, entry.rows), combined: false, centreLabel: entry.centre });
    }
    const unallocated = channelDetails.filter((detail) => detail.centre === "Unallocated");
    const hasUnallocatedData = unallocated.some((row) => row.plan || row.spend || row.sales || row.revenue);
    if (centreRows.length > 1 || hasUnallocatedData || centreRows.length === 0) {
      rows.push({ detail: mergeDetails(channel, channelDetails[0].centre, channelDetails), combined: true, centreLabel: "Combined" });
    }
  }
  return rows;
};

// Breakdown rows live in the same table as the month rows, so every number
// sits in the same column as the monthly figure above it.
function MonthDetailRows({ month, onViewSource, onViewRevenue }: {
  month: MonthRow;
  onViewSource: (detail: DetailRow, centreLabel: string) => void;
  onViewRevenue: (detail: DetailRow, channel: string, centreLabel: string) => void;
}) {
  if (!month.details.length) {
    return <tr className="platform-detail-row"><td colSpan={7}><div className="empty-detail"><Database size={18} />Detailed platform rows will appear after the historical files are imported.</div></td></tr>;
  }
  return (
    <>
      {channelRows(month.details).map(({ detail, combined, centreLabel }, index) => {
        const roas = roasPresentation(detail.spend, detail.revenue, detail.spendComplete);
        return (
          <tr key={`${month.month}-${detail.platform}-${centreLabel}-${index}`} className={`platform-detail-row ${combined ? "platform-detail-row--combined" : ""}`}>
            <td className="month-column">
              <div className="platform-detail-row__name">
              <span className="platform-name"><PlatformLogo platform={detail.platform} />{combined ? `${detail.platform} combined` : detail.platform}</span>
              {combined ? <span className="centre-badge centre-badge--combined centre-badge--flag" role="img" aria-label="All call centres" title="Both call centres plus unallocated sales and spend. Unallocated sales are not credited to either call centre.">🇪🇬 🇹🇷</span> : <CentreBadge centre={centreLabel as Centre} />}
              </div>
            </td>
            <td>{detail.plan ? money.format(detail.plan) : "—"}</td>
            <td>
              <button type="button" className="spend-provenance" title="Where this number came from" onClick={() => onViewSource(detail, centreLabel)}>
                <strong>{money.format(detail.spend)}</strong>
                <small>{detail.sourceEntries?.length ? `${detail.sourceCurrencies?.length ? [...new Set(detail.sourceCurrencies)].join("+") : detail.currency} converted · ${detail.source}${shortDate(detail.sourceUpdatedAt) ? ` ${shortDate(detail.sourceUpdatedAt)}` : ""}` : 'Spend not recorded'}</small>
              </button>
            </td>
            <td>
              <button type="button" className="spend-provenance" title="Every payment behind this number, with its lead source and attribution" onClick={() => onViewRevenue(detail, channelOf(detail), centreLabel)}>
                <strong>{money.format(detail.revenue)}</strong>
                <small>{detail.revenueSource}{shortDate(detail.revenueUpdatedAt) ? ` · ${shortDate(detail.revenueUpdatedAt)}` : ''}</small>
                {!combined && Boolean(detail.estimatedRevenue) && <small title="Actual payments, estimated call-centre allocation. Included above, not additional revenue.">{money.format(detail.estimatedRevenue ?? 0)} centre-estimated</small>}
              </button>
            </td>
            <td>
              <button type="button" className="spend-provenance" title="Every payment behind this number, with its lead source and attribution" onClick={() => onViewRevenue(detail, channelOf(detail), centreLabel)}>
                <strong>{detail.sales}</strong>
                {!combined && Boolean(detail.estimatedSales) && <small title="Call centre estimated from CRM-confirmed sales shares for the same channel and lead-created month; not an extra sale.">{detail.estimatedSales} centre-estimated</small>}
              </button>
            </td>
            <td><strong>{detail.sales > 0 ? money.format(detail.revenue / detail.sales) : "—"}</strong></td>
            <td title={roas.provisional ? 'Revenue ÷ recorded channel spend; verification pending' : roas.label}><strong className={roas.provisional ? "" : roas.positive ? "value-positive" : "value-low"}>{roas.value}</strong>{roas.provisional && <small className="metric-subline">Provisional</small>}</td>
          </tr>
        );
      })}
    </>
  );
}

function MonthlyTable({ filteredMonths, platformFilter, onViewSource, onViewRevenue, onViewMonthRevenue, onViewMonthSpend }: {
  filteredMonths: MonthRow[];
  platformFilter: "All platforms" | Channel;
  onViewSource: (month: MonthRow, detail: DetailRow, centreLabel: string) => void;
  onViewRevenue: (month: MonthRow, detail: DetailRow, channel: string, centreLabel: string) => void;
  onViewMonthRevenue: (month: MonthRow, channel: string) => void;
  onViewMonthSpend: (month: MonthRow, label: string) => void;
}) {
  const [openMonths, setOpenMonths] = useState<Set<string>>(() => new Set());
  const chronologicalMonths = useMemo(() => {
    const sorted = [...filteredMonths].sort((left, right) =>
      (left.monthKey ?? left.month).localeCompare(right.monthKey ?? right.month));
    if (platformFilter === "All platforms") return sorted;
    // Recompute every month from just the selected channel's rows so the
    // table's totals always match its filter.
    return sorted.map((month) => {
      const details = month.details.filter((detail) => channelOf(detail) === platformFilter);
      return {
        ...month,
        details,
        spendComplete: channelSpendComplete(details),
        change: 0,
        plan: details.reduce((sum, detail) => sum + detail.plan, 0),
        spend: details.reduce((sum, detail) => sum + detail.spend, 0),
        sales: details.reduce((sum, detail) => sum + detail.sales, 0),
        revenue: details.reduce((sum, detail) => sum + detail.revenue, 0),
      };
    });
  }, [filteredMonths, platformFilter]);

  const toggle = (month: string) => {
    setOpenMonths((current) => {
      const next = new Set(current);
      next.has(month) ? next.delete(month) : next.add(month);
      return next;
    });
  };

  return (
    <div className="monthly-table-wrap">
      <table className="monthly-table">
        <thead>
          <tr>
            <th className="month-column">Month</th>
            <th>Planned</th>
            <th>Actual</th>
            <th>Revenue so far</th>
            <th>Sales</th>
            <th>Cart value</th>
            <th>ROAS so far</th>
          </tr>
        </thead>
        <tbody>
          {chronologicalMonths.map((month) => {
            const open = openMonths.has(month.month);
            const roas = roasPresentation(month.spend, month.revenue, month.spendComplete, platformFilter !== "All platforms");
            const istanbul = month.details.filter((row) => row.centre === "Istanbul").reduce((sum, row) => sum + row.spend, 0);
            const cairo = month.details.filter((row) => row.centre === "Cairo").reduce((sum, row) => sum + row.spend, 0);
            return (
              <Fragment key={month.month}>
                <tr className={`month-row ${open ? "month-row--open" : ""}`}>
                  <td>
                    <button type="button" className="month-toggle" onClick={() => toggle(month.month)} aria-expanded={open}>
                      <ChevronDown size={17} />
                      <span><strong>{month.month}</strong><small>{open ? "Hide breakdown" : "Show breakdown"}</small></span>
                    </button>
                  </td>
                  <td>{money.format(month.plan)}</td>
                  <td>
                    <button type="button" className="spend-provenance" title="Where this month's spend came from, across every platform" onClick={() => onViewMonthSpend(month, platformFilter === "All platforms" ? "All platforms" : platformFilter)}>
                      <strong>{money.format(month.spend)}</strong>
                      {month.details.length > 0 && <small className="split-line">IST {compactMoney.format(istanbul)} · CAI {compactMoney.format(cairo)}</small>}
                    </button>
                  </td>
                  <td>
                    <button type="button" className="spend-provenance" title="Every payment behind this number, across every platform" onClick={() => onViewMonthRevenue(month, platformFilter === "All platforms" ? "All" : platformFilter)}>
                      <strong>{money.format(month.revenue)}</strong>
                      {month.change > 0 && <small className="change-line">+{money.format(month.change)} since update</small>}
                    </button>
                  </td>
                  <td>
                    <button type="button" className="spend-provenance" title="Every payment behind this number, across every platform" onClick={() => onViewMonthRevenue(month, platformFilter === "All platforms" ? "All" : platformFilter)}>
                      <strong>{month.sales}</strong>
                    </button>
                  </td>
                  <td><strong>{month.sales > 0 ? money.format(month.revenue / month.sales) : "—"}</strong></td>
                  <td title={roas.label}><strong className={roas.provisional ? "" : roas.positive ? "roas-value" : "roas-value roas-value--low"}>{roas.value}</strong>{roas.provisional && <small className="metric-subline">Provisional</small>}</td>
                </tr>
                {open && <MonthDetailRows key={`${month.month}-details`} month={month} onViewSource={(detail, centreLabel) => onViewSource(month, detail, centreLabel)} onViewRevenue={(detail, channel, centreLabel) => onViewRevenue(month, detail, channel, centreLabel)} />}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


const FEED_STATUS: Record<FeedPayment["status"], { label: string; title: string; tone: string }> = {
  IN_REPORT: { label: "In report", title: "Added to the report from the live feed.", tone: "fresh" },
  IN_EXPORT: { label: "In export", title: "A later monthly export includes this payment by its Payment ID, so the export's row is used.", tone: "fresh" },
  COVERED_BY_EXPORT: { label: "Covered by export", title: "Dated on or before the last export's cut-off. The export is the source of truth for that period, so the feed copy is kept but not added, to avoid double counting.", tone: "stale" },
  NOT_APPLIED: { label: "Not applied", title: "Received but not in the report. It is carried into the next import.", tone: "missing" },
};

function FeedPaymentsList({ payments, total }: { payments: FeedPayment[]; total: number }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? payments : payments.slice(0, 8);
  return (
    <div className="feed-payments">
      <div className="feed-payments__heading"><strong>Recent feed payments</strong><small>{total > payments.length ? `Latest ${payments.length} of ${total.toLocaleString()}` : `${total.toLocaleString()} received`}</small></div>
      <div className="feed-payments__scroll">
        <table>
          <thead><tr><th>Received</th><th>Payment</th><th>Patient</th><th>Amount</th><th>Paid</th><th>Lead month</th><th>Platform</th><th>Centre</th><th>Status</th></tr></thead>
          <tbody>{visible.map((payment) => {
            const status = FEED_STATUS[payment.status];
            return <tr key={payment.paymentId}>
              <td title={payment.receivedAt}>{freshnessAge(payment.receivedAt)}</td>
              <td className="feed-payments__id">{payment.paymentId}</td>
              <td>{payment.patientId}</td>
              <td className={payment.amountUsdCents < 0 ? "feed-payments__refund" : undefined}>{money2.format(payment.amountUsdCents / 100)}</td>
              <td>{payment.paymentDate ?? "—"}</td>
              <td>{payment.cohortMonth}</td>
              <td title={payment.leadSource}>{payment.platformId ? <span className="platform-name"><PlatformLogo platform={LEAD_PLATFORM_LABELS[payment.platformId] ?? payment.platformId} />{LEAD_PLATFORM_LABELS[payment.platformId] ?? payment.platformId}</span> : <span title="Not an advertising lead source">{payment.leadSource}</span>}</td>
              <td>{payment.centre ?? "—"}</td>
              <td title={status.title}><span className="feed-payments__status"><span className={`status-dot status-dot--${status.tone}`} />{status.label}</span></td>
            </tr>;
          })}</tbody>
        </table>
      </div>
      {payments.length > 8 && <button type="button" className="text-button" onClick={() => setShowAll((open) => !open)}>{showAll ? "Show fewer" : `Show all ${payments.length}`}</button>}
    </div>
  );
}

function CalculationPolicyDrawer({ onClose }: { onClose: () => void }) {
  return <div className="drawer-layer" role="presentation">
    <button className="drawer-backdrop" type="button" aria-label="Close calculation policy" onClick={onClose} />
    <aside className="drawer" aria-label="Advertising calculation policy">
      <div className="drawer__header"><div><p className="eyebrow">Reporting definitions</p><h2>Calculation policy</h2><p>How the report turns plans, uploads, currencies, and sales into comparable USD figures.</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close"><X size={19} /></button></div>
      <div className="drawer__body policy-list">
        <section><span><WalletCards size={17} /></span><div><h3>Planned spend</h3><p>Plans are entered and stored in USD by month, advertising platform, and call centre. Planning values never come from an advertising-platform export.</p></div></section>
        <section><span><Upload size={17} /></span><div><h3>Actual spend</h3><p>Actual spend comes from imported platform CSV files. Uploading the same file again is blocked by its file fingerprint. A corrected export replaces matching reporting scopes.</p></div></section>
        <section><span><CircleDollarSign size={17} /></span><div><h3>Currency conversion</h3><p>Native spend and revenue are divided by the stored monthly native-currency-per-USD average. Completed months are frozen; the current month uses its average to date.</p><code>USD value = native value ÷ monthly rate</code></div></section>
        <section><span><Gauge size={17} /></span><div><h3>ROAS and cohort maturity</h3><p>ROAS is revenue collected so far divided by actual spend for the same lead-generation month, platform, and call centre. Sales and revenue can increase as that cohort matures.</p><code>ROAS = revenue so far ÷ actual spend</code></div></section>
        <section><span><LockKeyhole size={17} /></span><div><h3>Corrections and traceability</h3><p>Imported spend is locked. Correct it by deleting the incorrect master entry or uploading the corrected source export. Administrative changes are retained in the audit trail.</p></div></section>
      </div>
      <div className="drawer__footer"><button type="button" className="primary-button" onClick={onClose}>Understood</button></div>
    </aside>
  </div>;
}

const currencySymbol = (currency: string): string => currency === "TRY" ? "₺" : currency === "GBP" ? "£" : "$";

const nativeMoney = (currency: string, minor: number): string =>
  `${currencySymbol(currency)}${(minor / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const originLabel = (entry: SpendProofEntry): { title: string; detail: string } => {
  if (entry.source === "Synced") {
    return {
      title: "Composio API synchronization",
      detail: entry.sourceReference?.startsWith("composio:")
        ? `Pulled directly from the advertising platform (account ${entry.sourceReference.split(":")[1]}).`
        : "Pulled directly from the advertising platform's API.",
    };
  }
  if (entry.source === "Uploaded") {
    return { title: "CSV import", detail: entry.sourceReference ? `Imported from ${entry.sourceReference}.` : "Imported from a platform export (filename not recorded for historical imports)." };
  }
  return { title: "Manual entry", detail: "Entered by an administrator before automated sources existed." };
};

function SpendProofBlock({ entry, fxRates, monthKey, platform, account }: { entry: SpendProofEntry; fxRates: FxRate[]; monthKey?: string; platform: string; account?: { name: string; external_id: string } }) {
  const origin = originLabel(entry);
  const fx = fxRates.find((rate) => rate.month === monthKey && rate.quote_currency === entry.nativeCurrency);
  const converted = entry.nativeCurrency !== "USD";
  const periodLabel = fx
    ? `${fx.period_start.slice(8, 10)} ${new Date(`${fx.period_start}T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })} – ${fx.period_end.slice(8, 10)} ${new Date(`${fx.period_end}T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })}`
    : null;
  return (
    <div className="proof-entry">
      <div className="spend-account-heading"><PlatformLogo platform={platform} /><div><strong>{account?.name || platform}</strong><small>{platform}{account ? ` · ${account.external_id}` : entry.accountId ? ` · ${entry.accountId}` : " · Account not recorded"}</small></div></div>
      <div className="proof-entry__origin">
        <span className={`source-badge source-badge--${entry.source.toLowerCase()}`}>{entry.source === "Synced" ? <RefreshCw size={12} /> : entry.source === "Uploaded" ? <Upload size={12} /> : <CircleDollarSign size={12} />}{origin.title}</span>
        <small>{origin.detail}</small>
      </div>
      {converted ? (
        <div className="proof-entry__calc">
          <div><span>Pulled in {entry.nativeCurrency}</span><strong>{nativeMoney(entry.nativeCurrency, entry.nativeAmountMinor)}</strong></div>
          <div className="proof-entry__op">÷</div>
          <div>
            <span>Rate used</span><strong>{entry.nativePerUsd.toFixed(entry.nativePerUsd >= 10 ? 4 : 6)}</strong>
            <small>{entry.nativeCurrency} per $1 · frozen when this entry was written</small>
          </div>
          <div className="proof-entry__op">=</div>
          <div><span>Reported</span><strong>{money.format(entry.amountUsdCents / 100)}</strong></div>
        </div>
      ) : (
        <div className="proof-entry__calc proof-entry__calc--direct">
          <div><span>Recorded in USD</span><strong>{money.format(entry.amountUsdCents / 100)}</strong><small>No currency conversion needed</small></div>
        </div>
      )}
      {converted && (
        <p className="proof-entry__rate-note">
          {fx
            ? `Monthly average for ${monthKey}: ${fx.native_per_usd.toFixed(fx.native_per_usd >= 10 ? 4 : 6)} · ${fx.status === "FROZEN" ? "frozen" : "month to date"} · ${fx.observation_count} daily observations (${periodLabel})`
            : `The stored ${monthKey} ${entry.nativeCurrency} average is not loaded; the frozen entry rate above is authoritative.`}
        </p>
      )}
      <p className="proof-entry__meta">Last written {entry.updatedAt.slice(0, 16).replace("T", " ")} UTC{entry.accountId ? ` · account ${entry.accountId}` : ""}</p>
    </div>
  );
}

// Every payment behind one revenue/sales cell: the CRM lead source, the
// attribution rule that routed it to the platform, and the Meta campaign
// evidence when the CRM carries it.
function RevenueProofDrawer({ scope, estimatedRevenue, channel, centreLabel, onClose }: {
  scope: { monthKey?: string; year?: string; label: string };
  estimatedRevenue?: number;
  channel: string;
  centreLabel: string;
  onClose: () => void;
}) {
  const [proof, setProof] = useState<RevenueProof | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.revenueProof({ month: scope.monthKey, year: scope.year }, channel, centreLabel)
      .then((result) => { if (!cancelled) setProof(result); })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The payment records could not be loaded."); });
    return () => { cancelled = true; };
  }, [scope.monthKey, scope.year, channel, centreLabel]);
  return (
    <div className="drawer-layer" role="presentation">
      <button className="drawer-backdrop" type="button" aria-label="Close revenue proof" onClick={onClose} />
      <aside className="drawer" aria-label="Revenue proof">
        <div className="drawer__header">
          <div>
            <p className="eyebrow">Revenue proof · {scope.label}</p>
            <h2>{channel === "All" ? "All platforms" : channel} · {centreLabel === "Combined" ? "All call centres" : centreLabel}</h2>
            {proof && <p>{proof.cohortRule}</p>}
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close"><X size={19} /></button>
        </div>
        <div className="drawer__body">
          {error && <div className="preview-notice"><span><AlertCircle size={16} /></span><div><strong>Could not load payments</strong><p>{error}</p></div></div>}
          {!proof && !error && <p role="status">Loading payment records…</p>}
          {proof && <>
            <div className="sales-preview-summary">
              <div><span>Paying patients</span><strong>{proof.summary.patients.toLocaleString()}</strong><small>counted as sales</small></div>
              <div><span>Payments</span><strong>{proof.summary.payments.toLocaleString()}</strong><small>incl. refunds</small></div>
              <div><span>Net revenue</span><strong>{money.format(proof.summary.revenueUsdCents / 100)}</strong><small>matches the report cell</small></div>
            </div>
            {Boolean(estimatedRevenue) && <div className="proof-note"><AlertCircle size={13} /><p>{money.format(estimatedRevenue ?? 0)} of this revenue comes from patients whose call centre was estimated from CRM-confirmed sales shares for the same channel and lead-created month. They are included below and tagged “centre estimated”.</p></div>}
            <div className="proof-note proof-note--muted"><AlertCircle size={13} /><p>{proof.utmNote}</p></div>
            {proof.truncated && <div className="proof-note"><AlertCircle size={13} /><p>Showing the largest {proof.patients.length.toLocaleString()} patients by revenue; the summary above still covers every payment.</p></div>}
            <div className="proof-list">
              {proof.patients.map((patient) => (
                <article className="proof-patient" key={patient.patientId}>
                  <div className="proof-patient__head">
                    <div><strong>Patient {patient.patientId}</strong><span>{scope.year ? `${patient.cohortMonth} cohort · ` : ""}{patient.platformLabel} · <CentreBadge centre={patient.centre} />{patient.centreEstimated && <em className="proof-tag" title="Call centre estimated from CRM-confirmed sales shares; the payments themselves are actual.">centre estimated</em>}{patient.country ? ` · ${patient.country}` : ""}{patient.language ? ` · ${patient.language}` : ""}</span></div>
                    <strong className={patient.totalUsdCents < 0 ? "value-low" : ""}>{money.format(patient.totalUsdCents / 100)}</strong>
                  </div>
                  <p className="proof-patient__reason">{patient.reason}</p>
                  {(patient.campaign || patient.adSet || patient.ad || patient.leadForm) && <dl className="proof-patient__evidence">
                    {patient.campaign && <div><dt>Campaign</dt><dd>{patient.campaign}</dd></div>}
                    {patient.adSet && <div><dt>Ad set</dt><dd>{patient.adSet}</dd></div>}
                    {patient.ad && <div><dt>Ad</dt><dd>{patient.ad}</dd></div>}
                    {patient.leadForm && <div><dt>Lead form</dt><dd>{patient.leadForm}</dd></div>}
                  </dl>}
                  <p className="proof-patient__payments">{patient.payments.map((payment, index) => <span key={index} className={payment.amountUsdCents < 0 ? "value-low" : undefined}>{payment.date ?? "No date"} · {money.format(payment.amountUsdCents / 100)}</span>)}</p>
                </article>
              ))}
              {!proof.patients.length && <p className="muted-table-note">No payments are attributed to this platform for this cohort month.</p>}
            </div>
          </>}
        </div>
      </aside>
    </div>
  );
}

// The spend behind any "spent" figure: one or more months, each with the
// merged reporting row for the scope and its underlying entries.
type SpendScope = { title: string; scopeLabel: string; periodLabel: string; groups: Array<{ month: MonthRow; detail: DetailRow }> };

const buildSpendScope = (title: string, scopeLabel: string, months: MonthRow[], include: (detail: DetailRow) => boolean): SpendScope | null => {
  const sorted = [...months].sort((left, right) => (left.monthKey ?? "").localeCompare(right.monthKey ?? ""));
  let groups = sorted.flatMap((month) => {
    const rows = month.details.filter(include);
    return rows.length ? [{ month, detail: mergeDetails(title, rows[0].centre, rows) }] : [];
  });
  // Across several months, months with nothing recorded are noise; keep one
  // so an entirely empty period still opens with its "nothing yet" message.
  if (groups.length > 1) {
    const recorded = groups.filter((group) => group.detail.spend !== 0 || group.detail.sourceEntries?.length);
    groups = recorded.length ? recorded : groups.slice(0, 1);
  }
  if (!groups.length) return null;
  const first = sorted[0].month;
  const last = sorted[sorted.length - 1].month;
  return { title, scopeLabel, periodLabel: first === last ? first : `${first} – ${last}`, groups };
};

function SpendSourceDrawer({ scope, canEdit, fxRates, onClose, onViewMaster, onUpload }: {
  scope: SpendScope;
  canEdit: boolean;
  fxRates: FxRate[];
  onClose: () => void;
  onViewMaster: () => void;
  onUpload: () => void;
}) {
  const multi = scope.groups.length > 1;
  const reported = scope.groups.reduce((sum, group) => sum + group.detail.spend, 0);
  const entryCount = scope.groups.reduce((sum, group) => sum + (group.detail.sourceEntries?.length ?? 0), 0);
  const totalCents = scope.groups.reduce((sum, group) => sum + (group.detail.sourceEntries ?? []).reduce((entrySum, entry) => entrySum + entry.amountUsdCents, 0), 0);
  const sources = new Set(scope.groups.map((group) => group.detail.source));
  const source: DetailRow["source"] = sources.size === 1 ? scope.groups[0].detail.source : "Mixed";
  const only = scope.groups[0].detail;
  const [accounts, setAccounts] = useState<Array<{ id: string; name: string; external_id: string; platform: string }>>([]);
  useEffect(() => {
    let cancelled = false;
    api.sources().then((result) => { if (!cancelled) setAccounts(result.platforms.flatMap((platform) => platform.accounts.map((account) => ({ ...account, platform: platform.label })))); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);
  const leave = (next: () => void) => { onClose(); next(); };
  return <div className="drawer-layer" role="presentation">
    <button className="drawer-backdrop" type="button" aria-label="Close spend proof" onClick={onClose} />
    <aside className="drawer drawer--compact" aria-label={`Spend source for ${scope.title} ${scope.scopeLabel}`}>
      <div className="drawer__header"><div><p className="eyebrow">Actual spend proof</p><h2>{scope.title} · {scope.scopeLabel}</h2><p>{scope.periodLabel}</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close"><X size={19} /></button></div>
      <div className="drawer__body">
        <div className="source-detail-hero"><SourceBadge source={source} /><strong>{money.format(reported)}</strong><p>Every underlying entry, its origin, and the exact conversion that produced this number.</p></div>
        {entryCount === 0
          ? <div className="login-security"><AlertCircle size={17} /><p>This reporting row has no actual-spend entry yet. {only.source === "Synced" ? "It will fill in with the next platform synchronization." : "Upload the platform export in Sources to populate it."}</p></div>
          : <>
            {scope.groups.map((group) => <Fragment key={group.month.monthKey ?? group.month.month}>
              {multi && <div className="proof-month"><span>{group.month.month}</span><strong>{money.format(group.detail.spend)}</strong></div>}
              {(group.detail.sourceEntries ?? []).map((entry, index) => {
                const account = accounts.find((item) => item.id === entry.accountId || item.external_id === entry.accountId);
                return <SpendProofBlock key={index} entry={entry} fxRates={fxRates} monthKey={group.month.monthKey} platform={account?.platform ?? group.detail.platform} account={account} />;
              })}
            </Fragment>)}
            {entryCount > 1 && <div className="proof-total"><span>Sum of {entryCount} entries{multi ? ` across ${scope.groups.length} months` : ""}</span><strong>{money.format(totalCents / 100)}</strong></div>}
            {Math.abs(totalCents / 100 - reported) > 0.01 && <div className="admin-status"><AlertCircle size={15} />The entry sum differs from the reported figure — report this discrepancy.</div>}
          </>}
      </div>
      <div className="drawer__footer drawer__footer--split"><button type="button" className="secondary-button" onClick={onClose}>Close</button>{canEdit && <div><button type="button" className="secondary-button" onClick={() => leave(onViewMaster)}><Database size={15} />View master entry</button><button type="button" className="primary-button" onClick={() => leave(onUpload)}><Upload size={15} />Upload corrected CSV</button></div>}</div>
    </aside>
  </div>;
}

function ReportView({ canEdit, onManageUploads, onViewMaster }: { canEdit: boolean; onManageUploads: () => void; onViewMaster: () => void }) {
  const [year, setYear] = useState("2026");
  const [centre, setCentre] = useState<"All call centres" | Centre>("All call centres");
  const [reportMonths, setReportMonths] = useState<MonthRow[]>([]);
  const [fxRates, setFxRates] = useState<FxRate[]>([]);
  const [summary, setSummary] = useState({ spend: 0, plan: 0, sales: 0, revenue: 0, roas: 0, spendComplete: false });
  const [loadedScope, setLoadedScope] = useState("");
  const [reportWarnings, setReportWarnings] = useState<string[]>([]);
  const loadSequence = useRef(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<"All platforms" | Channel>("All platforms");
  // Verified-scope totals: ROAS over the channels whose spend is recorded and
  // complete, so missing Google/Microsoft spend neither inflates the ratio
  // nor blanks the headline.
  const verifiedTotals = useMemo(() => {
    let spend = 0;
    let revenue = 0;
    for (const month of reportMonths) for (const detail of month.details) {
      if (detail.spendComplete === false) continue;
      spend += detail.spend;
      revenue += detail.revenue;
    }
    return { spend, revenue, roas: spend > 0 ? revenue / spend : 0 };
  }, [reportMonths]);
  const [freshness, setFreshness] = useState<Freshness | null>(null);
  const [spendScope, setSpendScope] = useState<SpendScope | null>(null);
  const [revenueProofTarget, setRevenueProofTarget] = useState<{ scope: { monthKey?: string; year?: string; label: string }; channel: string; centreLabel: string; estimatedRevenue?: number } | null>(null);

  const loadReport = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoadedScope("");
    setReportMonths([]);
    setFreshness(null);
    setLoadError(null);
    try {
      const [report, fx, fresh] = await Promise.all([api.report(year, centre), api.fx(year), api.freshness().catch(() => null)]);
      if (sequence !== loadSequence.current) return;
      setReportMonths(report.months);
      setReportWarnings(report.warnings ?? []);
      setSummary(report.totals);
      setFxRates(fx.rates);
      if (fresh) setFreshness(fresh);
      setLoadError(null);
      setLoadedScope(`${year}:${centre}`);
    } catch (caught) {
      if (sequence !== loadSequence.current) return;
      if (caught instanceof ApiClientError && caught.status === 401) {
        window.history.replaceState({}, "", "/login");
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }
      setLoadError(caught instanceof Error ? caught.message : "Reporting data could not be loaded.");
    }
  }, [centre, year]);

  useEffect(() => { void loadReport(); return () => { loadSequence.current += 1; }; }, [loadReport]);

  const channelSummaries = useMemo<ChannelSummary[]>(() => {
    const groups: ChannelSummary[] = [
      { id: "meta", label: "Meta combined", spend: 0, revenue: 0, sales: 0 },
      { id: "google", label: "Google combined", spend: 0, revenue: 0, sales: 0 },
      { id: "tiktok", label: "TikTok combined", spend: 0, revenue: 0, sales: 0 },
      { id: "microsoft", label: "Microsoft combined", spend: 0, revenue: 0, sales: 0 },
      { id: "x", label: "X Ads", spend: 0, revenue: 0, sales: 0 },
      { id: "pinterest", label: "Pinterest", spend: 0, revenue: 0, sales: 0 },
    ];
    const selectedYear = Number(year);
    const currentYear = Number(currentMonthKey.slice(0, 4));
    const cutoff = selectedYear < currentYear
      ? `${year}-12`
      : selectedYear === currentYear
        ? currentMonthKey
        : `${year}-00`;

    for (const month of reportMonths) {
      if (!month.monthKey || month.monthKey > cutoff) continue;
      for (const detail of month.details) {
        const normalizedPlatform = detail.platform.toLowerCase();
        const groupId = detail.platformId === "meta_en" || detail.platformId === "meta_other" || normalizedPlatform.startsWith("meta")
          ? "meta"
          : detail.platformId === "google" || normalizedPlatform.startsWith("google")
            ? "google"
            : detail.platformId === "tiktok" || normalizedPlatform.startsWith("tiktok")
              ? "tiktok"
              : detail.platformId === "microsoft" || normalizedPlatform.startsWith("microsoft")
                ? "microsoft"
                : detail.platformId === "x" || normalizedPlatform.startsWith("x ads")
                  ? "x"
                  : detail.platformId === "pinterest" || normalizedPlatform.startsWith("pinterest")
                    ? "pinterest"
                : null;
        if (!groupId) continue;
        const group = groups.find((candidate) => candidate.id === groupId);
        if (group && detail.spendComplete === false) group.spendComplete = false;
        if (!group) continue;
        group.spend += detail.spend;
        group.revenue += detail.revenue;
        group.sales += detail.sales;
      }
    }
    return groups;
  }, [reportMonths, year]);

  const CHANNEL_BY_CARD: Record<ChannelSummary["id"], Channel> = { meta: "Meta", google: "Google", tiktok: "TikTok", microsoft: "Microsoft", x: "X Ads", pinterest: "Pinterest" };
  const openCardProof = (channel: ChannelSummary) => setRevenueProofTarget({
    scope: { year, label: `${year} to date` },
    channel: CHANNEL_BY_CARD[channel.id],
    centreLabel: centre === "All call centres" ? "Combined" : centre,
  });
  const openCardSpend = (channel: ChannelSummary) => setSpendScope(buildSpendScope(channel.label, centre, reportMonths.filter((month) => (month.monthKey ?? "") <= currentMonthKey), (detail) => channelOf(detail) === CHANNEL_BY_CARD[channel.id]));
  const PRIMARY_CHANNEL_IDS: ChannelSummary["id"][] = ["meta", "google", "tiktok", "microsoft"];
  const primaryChannels = channelSummaries.filter((channel) => PRIMARY_CHANNEL_IDS.includes(channel.id));
  const secondaryChannels = channelSummaries.filter((channel) => !PRIMARY_CHANNEL_IDS.includes(channel.id));
  const [showAllChannels, setShowAllChannels] = useState(false);

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">{year} advertising report</p>
          <h1>Advertising performance</h1>
          <p>Plan, spend, and maturing cohort returns in one decision-ready view.</p>
        </div>
        <div className="page-heading__actions">
          <label className="select-control"><CalendarDays size={15} /><select aria-label="Report year" value={year} onChange={(event) => setYear(event.target.value)}><option>2026</option><option>2025</option><option>2024</option></select></label>
          <label className="select-control"><select aria-label="Call centre" value={centre} onChange={(event) => setCentre(event.target.value as typeof centre)}><option>All call centres</option><option>Istanbul</option><option>Cairo</option></select></label>
          {canEdit && <button type="button" className="secondary-button" onClick={onManageUploads}><Upload size={16} />Upload or replace data</button>}
        </div>
      </div>

      {freshness && <div className="freshness-strip">
        {[
          { label: "Meta", stamp: freshness.synced.meta, verb: "synced" },
          { label: "Google", stamp: freshness.synced.google, verb: "synced" },
          { label: "TikTok", stamp: freshness.synced.tiktok, verb: "synced" },
          { label: "Sales", stamp: freshness.salesImportedAt, verb: "imported" },
          { label: "Sales feed", stamp: freshness.salesFeedReceivedAt, verb: "received" },
          { label: "Microsoft", stamp: freshness.synced.microsoft ?? freshness.uploaded.microsoft, verb: freshness.synced.microsoft ? "synced" : "uploaded" },
        ].map((item, index) => <span key={item.label} className="freshness-strip__item">{index > 0 && <em>·</em>}<span className={`status-dot status-dot--${freshnessStatus(item.stamp)}`} /><strong>{item.label}</strong> {item.stamp ? `${item.verb} ${freshnessAge(item.stamp)}` : "never " + item.verb}</span>)}
      </div>}

      {loadError && <div className="preview-notice">
        <span><AlertCircle size={16} /></span>
        <div><strong>Reporting data could not be refreshed</strong><p>{loadError}</p></div>
      </div>}

      {loadedScope !== `${year}:${centre}` ? <p role="status">{loadError ? "Report unavailable. No financial totals are displayed." : "Loading verified reporting data…"}</p> : <>
      <section className="metric-grid" aria-label="Performance summary">
        <MetricCard label="Actual spend" value={money.format(summary.spend)} onValueClick={() => setSpendScope(buildSpendScope("All platforms", centre, reportMonths, () => true))} valueTitle="Every spend entry behind this number, month by month" note={`${summary.plan ? Math.round(summary.spend / summary.plan * 100) : 0}% of the full-year ${year} plan used`} icon={<CircleDollarSign size={18} />} />
        <MetricCard label="Revenue so far" value={money.format(summary.revenue)} note={`Collected from ${year} lead cohorts to date`} tone="positive" icon={<WalletCards size={18} />} />
        <MetricCard label="Sales" value={summary.sales.toLocaleString()} note="Paying patients from this year's cohorts" icon={<Users size={18} />} />
        <MetricCard label="Average ROAS" value={summary.spendComplete ? (summary.spend > 0 ? `${summary.roas.toFixed(2)}×` : "—") : verifiedTotals.spend > 0 ? `${verifiedTotals.roas.toFixed(2)}×` : "—"} note={summary.spendComplete ? `Weighted across every platform, ${year} to date` : verifiedTotals.spend > 0 ? "Channels with verified spend only" : 'Awaiting complete, verified spend'} tone={summary.spendComplete && summary.roas >= 1 ? "positive" : "warning"} icon={<Gauge size={18} />} />
      </section>

      <section className="channel-metric-section" aria-labelledby="channel-return-title">
        <div className="channel-metric-section__heading">
          <h2 id="channel-return-title">Channel return</h2>
          <p>Year to date · {centre}</p>
        </div>
        <div className={`channel-metric-grid ${showAllChannels && primaryChannels.length + secondaryChannels.length === 6 ? "channel-metric-grid--cols3" : ""}`}>
          {primaryChannels.map((channel) => <ChannelMetricCard key={channel.id} channel={channel} onViewRevenue={() => openCardProof(channel)} onViewSpend={() => openCardSpend(channel)} />)}
          {showAllChannels && secondaryChannels.map((channel) => <ChannelMetricCard key={channel.id} channel={channel} onViewRevenue={() => openCardProof(channel)} onViewSpend={() => openCardSpend(channel)} />)}
        </div>
        {secondaryChannels.length > 0 && (
          <button
            type="button"
            className="text-button channel-metric-section__toggle"
            aria-expanded={showAllChannels}
            onClick={() => setShowAllChannels((open) => !open)}
          >
            {showAllChannels
              ? <>Hide other platforms <ChevronUp size={14} /></>
              : <>Show {secondaryChannels.length} more platform{secondaryChannels.length === 1 ? "" : "s"} <ChevronDown size={14} /></>}
          </button>
        )}
      </section>

      <section className="report-surface">
        <div className="surface-header">
          <div>
            <h2>Monthly performance</h2>
            <p>Expand a month to see every platform and call centre.</p>
          </div>
          <div className="surface-tools">
            <label className="select-control"><select aria-label="Platform filter" value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value as typeof platformFilter)}><option>All platforms</option>{CHANNEL_ORDER.map((channel) => <option key={channel}>{channel}</option>)}</select></label>
          </div>
        </div>
        <MonthlyTable filteredMonths={reportMonths} platformFilter={platformFilter} onViewSource={(month, detail, centreLabel) => setSpendScope({ title: detail.platform, scopeLabel: centreLabel === "Combined" ? "All call centres" : centreLabel, periodLabel: month.month, groups: [{ month, detail }] })} onViewRevenue={(month, detail, channel, centreLabel) => setRevenueProofTarget({ scope: { monthKey: month.monthKey, label: `${month.month} cohort` }, channel, centreLabel, estimatedRevenue: detail.estimatedRevenue })} onViewMonthRevenue={(month, channel) => setRevenueProofTarget({ scope: { monthKey: month.monthKey, label: `${month.month} cohort` }, channel, centreLabel: centre === "All call centres" ? "Combined" : centre })} onViewMonthSpend={(month, label) => setSpendScope(buildSpendScope(label, centre, [month], () => true))} />
        <div className="table-footer">
          <p><span className="status-dot" />All source amounts are converted with their month’s stored average rate.</p>
          <button type="button" className="text-button" onClick={() => setPolicyOpen(true)}>View calculation policy <ArrowUpRight size={14} /></button>
        </div>
      </section>
      {reportWarnings.length > 0 && <details className="preview-notice preview-notice--footnote"><summary>Some spend is missing</summary><p>Channel ROAS uses the spend currently recorded and is labelled provisional until the gap is resolved. Channels with no spend remain unavailable. The overall ROAS covers channels with verified spend, so missing Google or Microsoft spend cannot inflate it.</p><p>Connected-platform gaps are checked hourly. Account-access problems and missing manual exports still need attention.</p><ul>{reportWarnings.map(warning => <li key={warning}>{warning}</li>)}</ul></details>}
      {policyOpen && <CalculationPolicyDrawer onClose={() => setPolicyOpen(false)} />}
      {spendScope && <SpendSourceDrawer scope={spendScope} canEdit={canEdit} fxRates={fxRates} onClose={() => setSpendScope(null)} onViewMaster={onViewMaster} onUpload={onManageUploads} />}
      {revenueProofTarget && <RevenueProofDrawer scope={revenueProofTarget.scope} estimatedRevenue={revenueProofTarget.estimatedRevenue} channel={revenueProofTarget.channel} centreLabel={revenueProofTarget.centreLabel} onClose={() => setRevenueProofTarget(null)} />}
      </>}
    </>
  );
}

type PlanCell = { platformId: string; centre: Centre; startsAt?: string };

const planCells: readonly PlanCell[] = [
  { platformId: "google", centre: "Istanbul" as Centre },
  { platformId: "google", centre: "Cairo" as Centre },
  { platformId: "meta_en", centre: "Istanbul" as Centre },
  { platformId: "meta_en", centre: "Cairo" as Centre },
  { platformId: "meta_other", centre: "Istanbul" as Centre },
  { platformId: "tiktok", centre: "Cairo" as Centre },
  { platformId: "microsoft", centre: "Cairo" as Centre },
  { platformId: "x", centre: "Cairo" as Centre, startsAt: "2026-08" },
  { platformId: "pinterest", centre: "Cairo" as Centre, startsAt: "2026-08" },
] as const;

type PlanChannel = "all" | "google" | "meta" | "tiktok" | "microsoft" | "x" | "pinterest";

const planChannels: Array<{ value: PlanChannel; label: string; platformIds: string[] }> = [
  { value: "all", label: "All channels", platformIds: planCells.map((cell) => cell.platformId) },
  { value: "google", label: "Google Ads", platformIds: ["google"] },
  { value: "meta", label: "Meta Ads", platformIds: ["meta_en", "meta_other"] },
  { value: "tiktok", label: "TikTok Ads", platformIds: ["tiktok"] },
  { value: "microsoft", label: "Microsoft Ads", platformIds: ["microsoft"] },
  { value: "x", label: "X Ads", platformIds: ["x"] },
  { value: "pinterest", label: "Pinterest", platformIds: ["pinterest"] },
];

const planValueKey = (month: string, platformId: string, centre: Centre) => `${month}|${platformId}|${centre}`;
const PLAN_PLATFORM_LABELS: Record<string, string> = { google: "Google", meta_en: "Meta EN", meta_other: "Meta Other", tiktok: "TikTok", microsoft: "Microsoft", x: "X Ads", pinterest: "Pinterest" };
const inPlanCell = (detail: DetailRow) => planCells.some((cell) => cell.platformId === detail.platformId && cell.centre === detail.centre);
const planCellActive = (month: string, cell: PlanCell) => !cell.startsAt || month >= cell.startsAt;

function EditableMoney({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [editing, setEditing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) input.current?.focus(); }, [editing]);

  if (!editing) {
    return <button type="button" className="plan-display" aria-label={`Edit planned amount ${planningMoney.format(Number(value || 0))}`} onClick={() => setEditing(true)}>{planningMoney.format(Number(value || 0))}</button>;
  }

  return <div className="plan-input"><span>$</span><input ref={input} aria-label="Planned amount" inputMode="decimal" value={value} onChange={(event) => {
    const cleaned = event.target.value.replace(/[^0-9.]/g, "");
    const [whole = "", ...decimalParts] = cleaned.split(".");
    onChange(decimalParts.length ? `${whole}.${decimalParts.join("").slice(0, 2)}` : whole);
  }} onBlur={() => setEditing(false)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === "Escape") setEditing(false); }} /></div>;
}

function PlanView() {
  const [planMonths, setPlanMonths] = useState<MonthRow[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [displayMode, setDisplayMode] = useState<"annual" | "quarterly">("annual");
  const [quarterChannel, setQuarterChannel] = useState<PlanChannel>("all");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [spendScope, setSpendScope] = useState<SpendScope | null>(null);
  const [planFxRates, setPlanFxRates] = useState<FxRate[]>([]);

  const loadPlan = useCallback(async () => {
    const [report, fx] = await Promise.all([api.report("2026"), api.fx("2026")]);
    setPlanFxRates(fx.rates);
    const nextValues: Record<string, string> = {};
    for (const month of report.months) {
      if (!month.monthKey) continue;
      for (const cell of planCells) {
        if (!planCellActive(month.monthKey, cell)) continue;
        const detail = month.details.find((candidate) => candidate.platformId === cell.platformId && candidate.centre === cell.centre);
        nextValues[planValueKey(month.monthKey, cell.platformId, cell.centre)] = String(detail?.plan ?? 0);
      }
    }
    setPlanMonths([...report.months].sort((left, right) => (left.monthKey ?? "").localeCompare(right.monthKey ?? "")));
    setValues(nextValues);
    setSavedValues(nextValues);
  }, []);

  useEffect(() => { void loadPlan().catch((error: unknown) => setMessage(error instanceof Error ? error.message : "The plan could not be loaded.")); }, [loadPlan]);

  const changedKeys = Object.keys(values).filter((key) => values[key] !== savedValues[key]);
  const remainingRows = planMonths.filter((month) => (month.monthKey ?? "") >= "2026-07");
  const remainingPlan = remainingRows.reduce((sum, month) => sum + planCells.reduce((cellSum, cell) => cellSum + (planCellActive(month.monthKey ?? "", cell) ? Number(values[planValueKey(month.monthKey ?? "", cell.platformId, cell.centre)] ?? 0) : 0), 0), 0);
  const fullYearPlan = planMonths.reduce((sum, month) => sum + planCells.reduce((cellSum, cell) => cellSum + (planCellActive(month.monthKey ?? "", cell) ? Number(values[planValueKey(month.monthKey ?? "", cell.platformId, cell.centre)] ?? 0) : 0), 0), 0);
  const actualSpend = planMonths.reduce((sum, month) => sum + month.details.reduce((detailSum, detail) => detailSum + detail.spend, 0), 0);
  const planVariance = actualSpend - fullYearPlan;
  const selectedChannel = planChannels.find((channel) => channel.value === quarterChannel) ?? planChannels[0];
  const visibleQuarterChannels = quarterChannel === "all" ? planChannels.filter((channel) => channel.value !== "all") : [selectedChannel];
  const quarters = Array.from({ length: 4 }, (_, index) => ({
    number: index + 1,
    months: planMonths.slice(index * 3, index * 3 + 3),
  }));

  const quarterlyRow = (month: MonthRow, channel = selectedChannel) => {
    const monthKey = month.monthKey ?? "";
    const channelPlanCells = planCells.filter((cell) => channel.platformIds.includes(cell.platformId) && planCellActive(monthKey, cell));
    const centreValues = (centre: Centre) => {
      const centreCells = channelPlanCells.filter((cell) => cell.centre === centre);
      const plan = centreCells.reduce((sum, cell) => sum + Number(values[planValueKey(monthKey, cell.platformId, cell.centre)] ?? 0), 0);
      const spend = month.details.reduce((sum, detail) => {
        if (detail.centre !== centre || !detail.platformId || !channel.platformIds.includes(detail.platformId)) return sum;
        return sum + detail.spend;
      }, 0);
      return { plan, spend };
    };
    const cairo = centreValues("Cairo");
    const istanbul = centreValues("Istanbul");
    const plan = cairo.plan + istanbul.plan;
    const spend = cairo.spend + istanbul.spend;
    return { cairo, istanbul, plan, spend, variance: spend - plan };
  };

  const savePlan = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.updatePlans(changedKeys.map((key) => {
        const [month, platformId, centre] = key.split("|") as [string, string, Centre];
        return { month, platformId, centre, amountUsdCents: Math.round(Number(values[key] || 0) * 100), expectedAmountUsdCents: Math.round(Number(savedValues[key] || 0) * 100) };
      }));
      await loadPlan();
      setMessage(`${changedKeys.length} planned amount${changedKeys.length === 1 ? "" : "s"} saved.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The plan could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">Budget control</p>
          <h1>Planned and actual advertising spend</h1>
          <p>Set the monthly USD plan and compare it with spend by platform and destination call centre.</p>
        </div>
        <div className="page-heading__actions"><button type="button" className="secondary-button" disabled={!changedKeys.length || saving} onClick={() => setValues(savedValues)}>Discard changes</button><button type="button" className="primary-button" disabled={!changedKeys.length || saving} onClick={savePlan}><Check size={16} />{saving ? "Saving…" : `Save plan${changedKeys.length ? ` (${changedKeys.length})` : ""}`}</button></div>
      </div>

      <section className="plan-summary">
        <div><span>Full-year planned</span><strong>{planningMoney.format(fullYearPlan)}</strong><small>Editable USD plan</small></div>
        <div><span>Actual spent</span><button type="button" className="plan-spent-link plan-spent-link--summary" title="Every spend entry behind this number, month by month" onClick={() => setSpendScope(buildSpendScope("All platforms", "All call centres", planMonths, () => true))}><strong>{planningMoney.format(actualSpend)}</strong></button><small>{fullYearPlan ? Math.round(actualSpend / fullYearPlan * 100) : 0}% of full-year plan</small></div>
        <div><span>Remaining plan</span><strong>{planningMoney.format(remainingPlan)}</strong><small>July–December planned</small></div>
        <div><span>Actual vs full plan</span><strong className={planVariance > 0 ? "value-low" : "value-positive"}>{planVariance > 0 ? "+" : ""}{planningMoney.format(planVariance)}</strong><small>{planVariance > 0 ? "Over full-year plan" : "Still available in plan"}</small></div>
      </section>

      <section className="report-surface">
        <div className="surface-header">
          <div><h2>{displayMode === "annual" ? "2026 monthly plan and spend" : `2026 quarterly plan · ${selectedChannel.label}`}</h2><p>{displayMode === "annual" ? "Edit the planned amount; imported actual spend is shown directly underneath." : "Four complete quarters, arranged for quick review and clean screenshots."}</p></div>
          <div className="surface-tools">
            {displayMode === "quarterly" && <label className="select-control plan-channel-filter"><span>Channel</span><select aria-label="Quarterly plan channel" value={quarterChannel} onChange={(event) => setQuarterChannel(event.target.value as PlanChannel)}>{planChannels.map((channel) => <option key={channel.value} value={channel.value}>{channel.label}</option>)}</select></label>}
            <div className="plan-view-switch" aria-label="Planning view">
              <button type="button" className={displayMode === "annual" ? "active" : undefined} aria-pressed={displayMode === "annual"} onClick={() => setDisplayMode("annual")}>Full year</button>
              <button type="button" className={displayMode === "quarterly" ? "active" : undefined} aria-pressed={displayMode === "quarterly"} onClick={() => setDisplayMode("quarterly")}>Quarterly</button>
            </div>
            <label className="select-control"><CalendarDays size={15} /><select defaultValue="2026"><option>2026</option><option>2025</option></select></label>
          </div>
        </div>
        {displayMode === "annual" ? <div className="plan-table-wrap">
          <table className="plan-table">
            <thead>
              <tr><th rowSpan={2}>Month</th><th colSpan={2}>Google</th><th colSpan={2}>Meta EN</th><th>Meta Other</th><th>TikTok</th><th>Microsoft</th><th>X Ads</th><th>Pinterest</th><th rowSpan={2}>Total plan / spent</th><th rowSpan={2}>Planned split</th></tr>
              <tr><th>Istanbul<small>Plan / spent</small></th><th>Cairo<small>Plan / spent</small></th><th>Istanbul<small>Plan / spent</small></th><th>Cairo<small>Plan / spent</small></th><th>Istanbul<small>Plan / spent</small></th><th>Cairo<small>Plan / spent</small></th><th>Cairo<small>Plan / spent</small></th><th>Cairo<small>From Aug</small></th><th>Cairo<small>From Aug</small></th></tr>
            </thead>
            <tbody>
              {planMonths.map((month) => {
                const monthKey = month.monthKey ?? "";
                const rowValues = planCells.map((cell) => planCellActive(monthKey, cell) ? Number(values[planValueKey(monthKey, cell.platformId, cell.centre)] ?? 0) : 0);
                const total = rowValues.reduce((sum, value) => sum + value, 0);
                const actualValues = planCells.map((cell) => month.details.find((detail) => detail.platformId === cell.platformId && detail.centre === cell.centre)?.spend ?? 0);
                const actualTotal = actualValues.reduce((sum, value) => sum + value, 0);
                const istanbulPlan = planCells.reduce((sum, cell, index) => sum + (cell.centre === "Istanbul" ? rowValues[index] : 0), 0);
                const istanbulPercent = total ? Math.round(istanbulPlan / total * 100) : 0;
                const cairoPercent = total ? 100 - istanbulPercent : 0;
                const isCurrentMonth = monthKey === currentMonthKey;
                return <tr key={monthKey} className={isCurrentMonth ? "plan-row--current" : undefined}><td><div className="plan-month"><strong>{month.month}</strong>{isCurrentMonth && <span>Current</span>}</div></td>{planCells.map((cell, index) => {
                  const key = planValueKey(monthKey, cell.platformId, cell.centre);
                  if (!planCellActive(monthKey, cell)) return <td key={key}><span className="plan-cell-inactive">Not active</span></td>;
                  return <td key={key}><div className="plan-cell"><EditableMoney value={values[key] ?? "0"} onChange={(value) => setValues((current) => ({ ...current, [key]: value }))} /><button type="button" className="plan-spent-link" title="Where this spend came from" onClick={() => setSpendScope(buildSpendScope(PLAN_PLATFORM_LABELS[cell.platformId] ?? cell.platformId, cell.centre, [month], (detail) => detail.platformId === cell.platformId && detail.centre === cell.centre))}>Spent <strong>{planningMoney.format(actualValues[index])}</strong></button></div></td>;
                })}<td><div className="plan-total-cell"><strong>{planningMoney.format(total)}</strong><button type="button" className="plan-spent-link" title="Every platform\u2019s spend this month" onClick={() => setSpendScope(buildSpendScope("All platforms", "All call centres", [month], inPlanCell))}>Spent {planningMoney.format(actualTotal)}</button></div></td><td>{total ? <div className="allocation-split"><div><span>IST</span><strong>{istanbulPercent}%</strong></div><span className="allocation-split__bar"><span style={{ width: `${istanbulPercent}%` }} /></span><div><span>CAI</span><strong>{cairoPercent}%</strong></div></div> : <span className="allocation-split__empty">—</span>}</td></tr>;
              })}
            </tbody>
          </table>
        </div> : <div className="quarter-plan-view">
          {quarters.map((quarter) => {
            const rows = quarter.months.map((month) => ({ month, values: quarterlyRow(month) }));
            const quarterPlan = rows.reduce((sum, row) => sum + row.values.plan, 0);
            const quarterSpend = rows.reduce((sum, row) => sum + row.values.spend, 0);
            const quarterVariance = quarterSpend - quarterPlan;
            const quarterIsCurrent = rows.some((row) => row.month.monthKey === currentMonthKey);
            const monthRange = rows.length ? `${rows[0].month.shortMonth}–${rows[rows.length - 1].month.shortMonth}` : "No data";
            return <section key={quarter.number} className={`quarter-plan-card${quarterIsCurrent ? " quarter-plan-card--current" : ""}`}>
              <div className="quarter-plan-card__header">
                <div><span>Quarter {quarter.number}</span><h3>Q{quarter.number} · {monthRange}</h3>{quarterIsCurrent && <small>Current quarter</small>}</div>
                <div className="quarter-plan-card__summary">
                  <div><span>Planned</span><strong>{planningMoney.format(quarterPlan)}</strong></div>
                  <div><span>Spent</span><button type="button" className="plan-spent-link plan-spent-link--summary" title="Every spend entry behind this quarter" onClick={() => setSpendScope(buildSpendScope(quarterChannel === "all" ? "All channels" : selectedChannel.label, "All call centres", quarter.months, (detail) => (detail.centre === "Cairo" || detail.centre === "Istanbul") && visibleQuarterChannels.some((channel) => channel.platformIds.includes(detail.platformId ?? ""))))}><strong>{planningMoney.format(quarterSpend)}</strong></button></div>
                  <div><span>Variance</span><strong className={quarterVariance > 0 ? "value-low" : "value-positive"}>{quarterVariance > 0 ? "+" : ""}{planningMoney.format(quarterVariance)}</strong></div>
                </div>
              </div>
              <div className="quarter-plan-table-wrap">
                <table className="quarter-plan-table">
                  <thead><tr><th>Month</th><th>Channel</th><th>Cairo</th><th>Istanbul</th><th>Total</th><th>Variance</th></tr></thead>
                  <tbody>{quarter.months.map((month) => <Fragment key={month.monthKey}>{visibleQuarterChannels.map((channel, channelIndex) => {
                    const row = quarterlyRow(month, channel);
                    const rowClasses = [month.monthKey === currentMonthKey ? "quarter-plan-row--current" : "", channelIndex === 0 ? "quarter-plan-row--month-start" : ""].filter(Boolean).join(" ");
                    return <tr key={`${month.monthKey}-${channel.value}`} className={rowClasses || undefined}>
                      {channelIndex === 0 && <td rowSpan={visibleQuarterChannels.length} className="quarter-plan-month-cell"><strong>{month.month.replace(" 2026", "")}</strong>{month.monthKey === currentMonthKey && <small>Current</small>}</td>}
                      <td className="quarter-plan-channel-cell"><span className="platform-name"><span className={`platform-dot platform-dot--${channel.value}`} />{channel.label}</span></td>
                      <td><strong>{planningMoney.format(row.cairo.plan)}</strong><button type="button" className="plan-spent-link" onClick={() => setSpendScope(buildSpendScope(channel.label, "Cairo", [month], (detail) => detail.centre === "Cairo" && channel.platformIds.includes(detail.platformId ?? "")))}>Spent {planningMoney.format(row.cairo.spend)}</button></td>
                      <td><strong>{planningMoney.format(row.istanbul.plan)}</strong><button type="button" className="plan-spent-link" onClick={() => setSpendScope(buildSpendScope(channel.label, "Istanbul", [month], (detail) => detail.centre === "Istanbul" && channel.platformIds.includes(detail.platformId ?? "")))}>Spent {planningMoney.format(row.istanbul.spend)}</button></td>
                      <td className="quarter-plan-total-cell"><strong>{planningMoney.format(row.plan)}</strong><button type="button" className="plan-spent-link" onClick={() => setSpendScope(buildSpendScope(channel.label, "All call centres", [month], (detail) => (detail.centre === "Cairo" || detail.centre === "Istanbul") && channel.platformIds.includes(detail.platformId ?? "")))}>Spent {planningMoney.format(row.spend)}</button></td>
                      <td><strong className={row.variance > 0 ? "value-low" : "value-positive"}>{row.variance > 0 ? "+" : ""}{planningMoney.format(row.variance)}</strong><span>{row.plan ? `${Math.round(row.spend / row.plan * 100)}% used` : "No plan"}</span></td>
                    </tr>;
                  })}</Fragment>)}</tbody>
                </table>
              </div>
            </section>;
          })}
        </div>}
        <div className="table-footer"><p><span className="status-dot" />{message ?? "Changing the plan never changes imported actual spend."}</p><button type="button" className="text-button">Review plan history <ArrowUpRight size={14} /></button></div>
      </section>
      {spendScope && <SpendSourceDrawer scope={spendScope} canEdit={false} fxRates={planFxRates} onClose={() => setSpendScope(null)} onViewMaster={() => undefined} onUpload={() => undefined} />}
    </>
  );
}

const sources = [
  { name: "Microsoft Ads", group: "MICROSOFT" as PlatformImportGroup, platformIds: ["microsoft"], state: "CSV spend export · allocated to Cairo" },
  { name: "X Ads", group: "X" as PlatformImportGroup, platformIds: ["x"], state: "CSV spend export · Cairo only · active from August 2026" },
  { name: "Pinterest", group: "PINTEREST" as PlatformImportGroup, platformIds: ["pinterest"], state: "CSV spend export · Cairo only · active from August 2026" },
];

function AccessDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"ADMINISTRATOR" | "VIEWER">("VIEWER");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.createUser({ name, email, role });
      await onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The person could not be added.");
    } finally {
      setSaving(false);
    }
  };

  return <div className="drawer-layer" role="presentation">
    <button className="drawer-backdrop" type="button" aria-label="Close access panel" onClick={onClose} />
    <aside className="drawer drawer--compact" aria-label="Add Google account access">
      <div className="drawer__header"><div><p className="eyebrow">Google access</p><h2>Add an approved person</h2><p>They must sign in using this exact Google account.</p></div><button type="button" className="icon-button" onClick={onClose}><X size={19} /></button></div>
      <div className="drawer__body">
        <label className="field-label">Full name<input className="field-control" value={name} onChange={(event) => setName(event.target.value)} placeholder="Khawla Reports" /></label>
        <label className="field-label">Google account email<input className="field-control" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@dentspa.com.tr" /></label>
        <label className="field-label">Permission<select className="field-control" value={role} onChange={(event) => setRole(event.target.value as typeof role)}><option value="VIEWER">Viewer — report access only</option><option value="ADMINISTRATOR">Administrator — can update and import</option></select></label>
        <div className="login-security"><ShieldCheck size={17} /><p>Adding an email does not send an invitation. It permits that Google account to sign in.</p></div>
        {error && <div className="form-error"><AlertCircle size={15} />{error}</div>}
      </div>
      <div className="drawer__footer"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button type="button" className="primary-button" disabled={saving || !name.trim() || !email.trim()} onClick={save}><Check size={16} />{saving ? "Adding…" : "Add person"}</button></div>
    </aside>
  </div>;
}

function RoleDrawer({ user, onClose, onSaved }: {
  user: ApprovedUser;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [role, setRole] = useState<ApprovedUser["role"]>(user.role);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateUserRole(user.id, role);
      await onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The access level could not be changed.");
    } finally {
      setSaving(false);
    }
  };

  return <div className="drawer-layer" role="presentation">
    <button className="drawer-backdrop" type="button" aria-label="Close access-level panel" onClick={onClose} />
    <aside className="drawer drawer--compact" aria-label="Change Google account access level">
      <div className="drawer__header"><div><p className="eyebrow">Google access</p><h2>Change access level</h2><p>{user.name} · {user.email}</p></div><button type="button" className="icon-button" onClick={onClose}><X size={19} /></button></div>
      <div className="drawer__body">
        <button type="button" className={`role-choice ${role === "ADMINISTRATOR" ? "role-choice--selected" : ""}`} onClick={() => setRole("ADMINISTRATOR")}><ShieldCheck size={19} /><span><strong>Administrator</strong><small>Can manage plans, uploads, master data, rates, source reviews, and access.</small></span>{role === "ADMINISTRATOR" && <Check size={17} />}</button>
        <button type="button" className={`role-choice ${role === "VIEWER" ? "role-choice--selected" : ""}`} onClick={() => setRole("VIEWER")}><Users size={19} /><span><strong>Viewer</strong><small>Can open the advertising report but cannot change reporting data.</small></span>{role === "VIEWER" && <Check size={17} />}</button>
        <div className="login-security"><ShieldAlert size={17} /><p>The system will not allow the final active administrator to be changed to Viewer.</p></div>
        {error && <div className="form-error"><AlertCircle size={15} />{error}</div>}
      </div>
      <div className="drawer__footer"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button type="button" className="primary-button" disabled={saving || role === user.role} onClick={() => void save()}><Check size={16} />{saving ? "Saving…" : "Save access level"}</button></div>
    </aside>
  </div>;
}

function PlatformImportDrawer({
  preview,
  duplicate,
  onClose,
  onImported,
}: {
  preview: PlatformImportPreview;
  duplicate: boolean;
  onClose: () => void;
  onImported: (preview: PlatformImportPreview) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isImported = preview.status === "IMPORTED";
  const monthsInFile = [...new Set(preview.entries.map((entry) => entry.month))].sort();

  const commit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.commitPlatform(preview.id);
      onImported(result.imported);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The spend file could not be imported.");
    } finally {
      setSaving(false);
    }
  };

  return <div className="drawer-layer" role="presentation">
    <button className="drawer-backdrop" type="button" aria-label="Close spend import preview" onClick={onClose} />
    <aside className="drawer" aria-label="Platform spend import preview">
      <div className="drawer__header"><div><p className="eyebrow">{preview.platformGroup} spend import</p><h2>Review before adding spend</h2><p>{preview.fileName}</p></div><button type="button" className="icon-button" onClick={onClose}><X size={19} /></button></div>
      <div className="drawer__body">
        <div className={`import-verdict ${isImported ? "import-verdict--success" : ""}`}>
          {isImported ? <Check size={18} /> : <FileSpreadsheet size={18} />}
          <div><strong>{isImported ? "Already imported" : "Ready to import"}</strong><p>{isImported ? "This exact export is already included, so it will not be duplicated." : "Call-centre routing, currency conversion, and duplicate checks have been applied."}</p></div>
        </div>
        <div className="import-metrics">
          <div><span>Actual spend</span><strong>{money.format(preview.totalUsdCents / 100)}</strong><small>Converted to reporting USD</small></div>
          <div><span>Source rows</span><strong>{preview.rowCount.toLocaleString()}</strong><small>{preview.entries.length} reporting entries</small></div>
        </div>
        <section className="import-checks">
          <div><span>Month coverage</span><strong>{monthsInFile.length ? `${monthsInFile[0]}${monthsInFile.length > 1 ? ` – ${monthsInFile.at(-1)}` : ""}` : "—"}</strong></div>
          <div><span>Call-centre routing</span><strong>{[...new Set(preview.entries.map((entry) => entry.centre))].join(", ")}</strong></div>
          <div><span>Exchange rates</span><strong>Stored monthly averages</strong></div>
          <div><span>Duplicate protection</span><strong>{duplicate ? "Exact file found" : "New file"}</strong></div>
        </section>
        <div className="import-note"><LockKeyhole size={16} /><p>Imported spend is locked. Correct the advertising-platform export and upload it again if the source data is wrong.</p></div>
        {preview.warnings.length > 0 && <div className="import-warnings"><strong>Check before importing</strong>{preview.warnings.map((warning) => <p key={warning}><AlertCircle size={14} />{warning}</p>)}</div>}
        {error && <div className="form-error"><AlertCircle size={15} />{error}</div>}
      </div>
      <div className="drawer__footer"><button type="button" className="secondary-button" onClick={onClose}>{isImported ? "Done" : "Cancel"}</button>{!isImported && <button type="button" className="primary-button" disabled={saving} onClick={() => void commit()}><Check size={16} />{saving ? "Importing…" : "Import actual spend"}</button>}</div>
    </aside>
  </div>;
}

const MCP_STATUS_LABELS: Record<McpConnectionRequest["status"], string> = {
  PENDING: "Waiting", APPROVED: "Approved", COMPLETED: "Connected", DENIED: "Denied", EXPIRED: "Expired",
};

function McpConnectionRequests({ onChange }: { onChange?: () => void }) {
  const [requests, setRequests] = useState<McpConnectionRequest[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => api.mcpConnectionRequests().then((result) => { setRequests(result.requests); setError(null); })
    .catch((caught) => setError(caught instanceof Error ? caught.message : "Connection requests could not load.")), []);
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load]);
  const decide = async (id: string, decision: "approve" | "deny") => {
    setBusy(id);
    try { await api.decideMcpConnection(id, decision); } catch (caught) { setError(caught instanceof Error ? caught.message : "The decision could not be saved."); }
    setBusy(null);
    await load();
    onChange?.();
  };
  const pending = requests?.filter((request) => request.status === "PENDING") ?? [];
  const recent = requests?.filter((request) => request.status !== "PENDING").slice(0, 10) ?? [];
  return (
    <section className="report-surface admin-grid__full mcp-requests">
      <div className="surface-header"><div><h2>AI connection requests</h2><p>People connecting an AI app (Claude, ChatGPT…) to DentSpa data through MCP. Viewers' connections start only after an administrator approves them here. Check the destination is the AI app they use.</p></div></div>
      {error && <div className="admin-status"><AlertCircle size={15} />{error}</div>}
      {requests === null ? <p className="muted-table-note">Loading…</p> : <table className="access-table">
        <thead><tr><th>Person</th><th>App</th><th>Sends access to</th><th>Requested</th><th>Status</th><th /></tr></thead>
        <tbody>
          {pending.length === 0 && recent.length === 0 && <tr><td colSpan={6}><span className="muted-table-note">No connection requests in the last 30 days.</span></td></tr>}
          {[...pending, ...recent].map((request) => <tr key={request.id} className={request.status === "PENDING" ? "mcp-requests__pending" : undefined}>
            <td><strong>{request.userName}</strong><small className="master-reference">{request.userEmail}</small></td>
            <td>{request.appName}</td>
            <td><strong>{request.destination}</strong></td>
            <td title={request.requestedAt}>{freshnessAge(request.requestedAt)}</td>
            <td>{MCP_STATUS_LABELS[request.status]}{request.decidedBy && <small className="master-reference">by {request.decidedBy}</small>}</td>
            <td>{request.status === "PENDING" && <span className="mcp-requests__actions">
              <button type="button" className="secondary-button secondary-button--small" disabled={busy !== null} onClick={() => void decide(request.id, "deny")}>Deny</button>
              <button type="button" className="primary-button primary-button--small" disabled={busy !== null} onClick={() => void decide(request.id, "approve")}><Check size={14} />Approve</button>
            </span>}</td>
          </tr>)}
        </tbody>
      </table>}
    </section>
  );
}

function DataView({ onOpenMaster, focusUploads, onConnectionsChanged }: { onOpenMaster: () => void; focusUploads: number; onConnectionsChanged?: () => void }) {
  const [sourceData, setSourceData] = useState<Awaited<ReturnType<typeof api.sources>>["platforms"]>([]);
  const [userData, setUserData] = useState<Awaited<ReturnType<typeof api.users>>["users"]>([]);
  const [composioData, setComposioData] = useState<ComposioStatus | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [roleUser, setRoleUser] = useState<ApprovedUser | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [salesStatus, setSalesStatus] = useState<SalesImportStatus | null>(null);
  const [salesPaymentsFile, setSalesPaymentsFile] = useState<File | null>(null);
  const [salesLeadFile, setSalesLeadFile] = useState<File | null>(null);
  const [salesPreview, setSalesPreview] = useState<SalesImportPreview | null>(null);
  const [confirmFullSnapshot, setConfirmFullSnapshot] = useState(false);
  const [salesCsvCache, setSalesCsvCache] = useState<{ paymentsCsv: string; leadSourcesCsv: string; leadCounts: { rows: LeadCountRow[]; contacts: number } } | null>(null);
  const [salesBusy, setSalesBusy] = useState<"preview" | "import" | null>(null);
  const [salesFileNote, setSalesFileNote] = useState<string | null>(null);
  const [syncingAds, setSyncingAds] = useState<"META" | "GOOGLE" | "TIKTOK" | null>(null);
  const [syncMonth, setSyncMonth] = useState(currentMonthKey);
  const [googleSyncAccount, setGoogleSyncAccount] = useState("");
  const [platformImportPreview, setPlatformImportPreview] = useState<{ preview: PlatformImportPreview; duplicate: boolean } | null>(null);
  const [selectedPlatformGroup, setSelectedPlatformGroup] = useState<PlatformImportGroup | null>(null);
  const [importingPlatformFile, setImportingPlatformFile] = useState(false);
  const platformFileInput = useRef<HTMLInputElement>(null);
  const uploadSection = useRef<HTMLElement>(null);
  const fallbackImports = useRef<HTMLDetailsElement>(null);

  const loadAdmin = useCallback(async () => {
    const [sourceResult, usersResult, composioResult, salesResult] = await Promise.allSettled([
      api.sources(), api.users(), api.composioStatus(), api.salesImportStatus(),
    ]);
    if (sourceResult.status === "fulfilled") setSourceData(sourceResult.value.platforms);
    if (usersResult.status === "fulfilled") setUserData(usersResult.value.users);
    if (composioResult.status === "fulfilled") setComposioData(composioResult.value);
    if (salesResult.status === "fulfilled") setSalesStatus(salesResult.value);
    const failure = [sourceResult, usersResult, composioResult, salesResult].find((result) => result.status === "rejected");
    if (failure && failure.status === "rejected") throw failure.reason instanceof Error ? failure.reason : new Error(String(failure.reason));
  }, []);

  useEffect(() => { void loadAdmin().catch((error: unknown) => setStatus(error instanceof Error ? error.message : "Administration data could not be loaded.")); }, [loadAdmin]);
  useEffect(() => {
    if (!focusUploads) return;
    if (fallbackImports.current) fallbackImports.current.open = true;
    window.requestAnimationFrame(() => uploadSection.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [focusUploads]);

  const refreshAdvertising = async (platformGroup: "META" | "GOOGLE" | "TIKTOK") => {
    setSyncingAds(platformGroup);
    setStatus(null);
    try {
      const dateFrom = `${syncMonth}-01`;
      const dateTo = syncMonth === currentMonthKey ? currentDateKey : lastDateOfMonth(syncMonth);
      const result = await api.syncComposio(platformGroup, dateFrom, dateTo, platformGroup === "GOOGLE" ? googleSyncAccount || undefined : undefined);
      await loadAdmin();
      const rows = result.data.results.reduce((sum, item) => sum + item.rowCount, 0);
      const accounts = result.data.results.reduce((sum, item) => sum + item.accountCount, 0);
      const failures = result.data.errors.map((item) => `${item.platformGroup}: ${item.message}`).join(" ");
      setStatus(failures
        ? `${rows.toLocaleString()} advertising rows updated across ${accounts} accounts. ${failures}`
        : `${rows.toLocaleString()} advertising rows updated across ${accounts} accounts for ${result.data.dateFrom} to ${result.data.dateTo}.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Advertising data could not be synchronized.");
    } finally {
      setSyncingAds(null);
    }
  };

  const openPlatformFilePicker = (group: PlatformImportGroup) => {
    setSelectedPlatformGroup(group);
    window.setTimeout(() => platformFileInput.current?.click(), 0);
  };

  const previewPlatformFile = async (file: File) => {
    if (!selectedPlatformGroup) return;
    setImportingPlatformFile(true);
    setStatus(null);
    try {
      const result = await api.previewPlatform(selectedPlatformGroup, file.name, await file.text());
      setPlatformImportPreview(result);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "The platform export could not be read.");
    } finally {
      setImportingPlatformFile(false);
      setSelectedPlatformGroup(null);
      if (platformFileInput.current) platformFileInput.current.value = "";
    }
  };

  // Route a chosen or dropped CSV to the correct slot by its header row, so
  // picking the files in the wrong order (or the wrong slot) still works.
  const assignSalesFiles = async (files: FileList | File[]) => {
    const notes: string[] = [];
    let payments = salesPaymentsFile;
    let leads = salesLeadFile;
    for (const file of Array.from(files).slice(0, 4)) {
      const start = new Uint8Array(await file.slice(0, 4).arrayBuffer());
      if (/\.xlsx?$/i.test(file.name) || (start[0] === 0x50 && start[1] === 0x4b) || (start[0] === 0xd0 && start[1] === 0xcf)) {
        notes.push(`“${file.name}” is an Excel file. In Zoho, export the underlying data as CSV instead.`);
        continue;
      }
      const header = (await file.slice(0, 8_192).text()).split(/\r?\n/)[0]?.toLowerCase().replace(/[^a-z0-9]+/g, " ") ?? "";
      // Both exports carry Payment Amount; only the payments export has
      // Virgin Branch and only the lead export has Contact Lead Source.
      if (header.includes("virgin branch")) payments = file;
      else if (header.includes("lead source")) leads = file;
      else notes.push(`“${file.name}” doesn't look like either Zoho export — its first row matches neither query's columns. Re-export the underlying data without changing columns.`);
    }
    setSalesPaymentsFile(payments);
    setSalesLeadFile(leads);
    setSalesFileNote(notes.length ? notes.join(" ") : null);
    if (payments && leads && !notes.length) void previewSales(payments, leads);
  };

  const previewSales = async (paymentsFile = salesPaymentsFile, leadFile = salesLeadFile) => {
    setConfirmFullSnapshot(false);
    if (!paymentsFile || !leadFile) return;
    setSalesBusy("preview");
    setStatus(null);
    try {
      const paymentsCsv = await paymentsFile.text();
      const fullLeadCsv = await leadFile.text();
      // Count every lead for CPL before the file is trimmed to paying patients.
      const leadCounts = countLeads(fullLeadCsv);
      const leadSourcesCsv = trimLeadSourcesCsv(paymentsCsv, fullLeadCsv).csv;
      const result = await api.previewSalesImport({
        paymentsFile: paymentsFile.name, paymentsCsv,
        leadSourcesFile: leadFile.name, leadSourcesCsv,
      });
      setSalesCsvCache({ paymentsCsv, leadSourcesCsv, leadCounts });
      setSalesPreview(result.preview);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "The sales exports could not be read.");
    } finally {
      setSalesBusy(null);
    }
  };

  const commitSales = async () => {
    if (!salesPreview || !salesCsvCache || !salesPaymentsFile || !salesLeadFile) return;
    setSalesBusy("import");
    try {
      const result = await api.commitSalesImport({
        confirmFullSnapshot,
        jobId: salesPreview.id,
        paymentsFile: salesPaymentsFile.name, paymentsCsv: salesCsvCache.paymentsCsv,
        leadSourcesFile: salesLeadFile.name, leadSourcesCsv: salesCsvCache.leadSourcesCsv,
      });
      const { leadCounts } = salesCsvCache;
      const leadsNote = leadCounts.rows.length
        ? await api.importLeadCounts({ fileName: salesLeadFile.name, contacts: leadCounts.contacts, rows: leadCounts.rows })
          .then((leads) => ` ${leads.data.adLeads.toLocaleString()} advertising leads updated for CPL.`)
          .catch((error) => ` Lead counts for CPL were not updated: ${error instanceof Error ? error.message : "unknown error"}`)
        : " No lead counts found in the lead-source file, so CPL was not updated.";
      setSalesPreview(null);
      setSalesCsvCache(null);
      setSalesPaymentsFile(null);
      setSalesLeadFile(null);
      setSalesFileNote(null);
      await loadAdmin();
      setStatus(`${result.data.paymentRows.toLocaleString()} payments imported. Sales and revenue in the report now come from this CRM snapshot.${leadsNote}`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "The sales import could not be completed.");
    } finally {
      setSalesBusy(null);
    }
  };

  return (
    <>
      <div className="page-heading">
        <div><p className="eyebrow">Administration</p><h1>Sources and access</h1><p>Bring in spend, verify currency rates, and control who can view the report.</p></div>
      </div>

      <input ref={platformFileInput} className="visually-hidden" type="file" accept=".csv,text/csv" onChange={(event) => { const file = event.target.files?.[0]; if (file) void previewPlatformFile(file); }} />

      <div className="admin-grid">
        <section
          className="report-surface admin-grid__full sales-import-card"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => { event.preventDefault(); if (event.dataTransfer.files.length) void assignSalesFiles(event.dataTransfer.files); }}
        >
          <div className="surface-header">
            <div><h2>Sales &amp; revenue import</h2><p>The report's sales and revenue come from this snapshot. Advertising spend syncs automatically — this import is the one step that stays manual.</p></div>
            <div className="sales-import-last">
              <span className={`status-dot status-dot--${freshnessStatus(salesStatus?.lastImport?.importedAt ?? null)}`} />
              {salesStatus?.lastImport
                ? <div><span>Imported {freshnessAge(salesStatus.lastImport.importedAt)}</span><strong>{salesStatus.lastImport.paymentRows.toLocaleString()} payments · {money.format(salesStatus.lastImport.totalRevenueUsdCents / 100)}</strong><small>{salesStatus.lastImport.importedAt.slice(0, 16).replace("T", " ")} UTC</small></div>
                : <div><span>Never imported</span><strong>The report has no CRM sales yet</strong></div>}
            </div>
          </div>
          <div className="sales-feed-status">
            <span className={`status-dot status-dot--${freshnessStatus(salesStatus?.feed.lastReceivedAt ?? null)}`} />
            <p>
              <strong>Live payment feed:</strong>{" "}
              {salesStatus?.feed.lastReceivedAt
                ? <>last payment received {freshnessAge(salesStatus.feed.lastReceivedAt)} · {salesStatus.feed.payments.toLocaleString()} payment{salesStatus.feed.payments === 1 ? "" : "s"} received so far</>
                : "no payments received yet"}
              {salesStatus?.lastImport && <>. The last export covers payments through {salesStatus.lastImport.paymentsThrough ?? "an unknown date"}; later feed payments are kept on the next import{salesStatus.lastImport.hasPaymentIds ? ", matched by Payment ID." : ". It has no Payment ID column, so feed payments are matched by date — add Payment ID to the export for exact matching."}</>}
            </p>
          </div>
          {salesStatus && salesStatus.feed.recent.length > 0 && <FeedPaymentsList payments={salesStatus.feed.recent} total={salesStatus.feed.payments} />}
          <div className="sales-import-steps">
            <div className="sales-import-step">
              <span className="sales-import-step__number">1</span>
              <div>
                <strong>Export from Zoho Analytics</strong>
                <p>Open <strong>“Ads Sold Rate Report Linked by Payments”</strong> and export the <strong>underlying data</strong> of both queries — full history, unfiltered:</p>
                <p className="sales-import-step__queries"><FileSpreadsheet size={12} /> Ads Int.Yas Payment Made By Payment Date<br /><FileSpreadsheet size={12} /> Ads Int.Yas Lead Source of Payment</p>
              </div>
            </div>
            <div className="sales-import-step">
              <span className="sales-import-step__number">2</span>
              <div className="sales-import-step__files">
                <strong>Drop both CSV files here</strong>
                <p>Select or drop the two exports together, in any order. Each file is recognised by its columns.</p>
                <label className={`sales-dropzone ${salesPaymentsFile && salesLeadFile ? "sales-dropzone--ready" : ""}`}>
                  <input type="file" accept=".csv,text/csv" multiple onChange={(event) => { if (event.target.files?.length) void assignSalesFiles(event.target.files); event.target.value = ""; }} />
                  <Upload size={16} />
                  <span>{salesPaymentsFile || salesLeadFile ? "Drop or choose again to replace a file" : "Drop both files, or click to choose them"}</span>
                </label>
                <ul className="sales-file-status">
                  <li className={salesPaymentsFile ? "sales-file-status--found" : undefined}>{salesPaymentsFile ? <Check size={13} /> : <CircleDashed size={13} />}<span>Payments</span><em>{salesPaymentsFile?.name ?? "waiting for file"}</em></li>
                  <li className={salesLeadFile ? "sales-file-status--found" : undefined}>{salesLeadFile ? <Check size={13} /> : <CircleDashed size={13} />}<span>Lead sources</span><em>{salesLeadFile?.name ?? "waiting for file"}</em></li>
                </ul>
                {salesFileNote && <p className="sales-import-mismatch"><AlertCircle size={13} />{salesFileNote}</p>}
              </div>
            </div>
            <div className="sales-import-step">
              <span className="sales-import-step__number">3</span>
              <div>
                <strong>Preview, then publish</strong>
                <p>The preview opens by itself once both files are recognised. Nothing changes until you confirm and publish.</p>
                <button type="button" className="primary-button" disabled={!salesPaymentsFile || !salesLeadFile || salesBusy !== null} onClick={() => void previewSales()}>
                  <Upload size={15} />{salesBusy === "preview" ? "Reading exports…" : "Preview import"}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="report-surface admin-grid__full connected-ads-card">
          <div className="surface-header">
            <div><h2>Microsoft Ads direct connection</h2><p>DENTSPA LTD · GBP · Cairo. Preview and sync a chosen month range without changing CRM revenue.</p></div>
            <a className="secondary-button" href="/api/integrations/microsoft-ads/spend">Sync Microsoft Ads</a>
            <a className="secondary-button" href="/api/integrations/microsoft-ads">Connection settings</a>
          </div>
          <div className="surface-header">
            <div><h2>Advertising platform sync</h2><p>Choose one month, then update only the advertising platform you select.</p></div>
            <label className="select-control sync-month-control"><CalendarDays size={15} /><span>Month</span><input aria-label="Month to synchronize" type="month" min="2025-01" max={currentMonthKey} value={syncMonth} onChange={(event) => setSyncMonth(event.target.value)} /></label>
          </div>
          <div className="connected-ads-grid">
            {(composioData?.platforms ?? []).map((platform) => {
              const name = platform.platformGroup === "META" ? "Meta Ads" : platform.platformGroup === "GOOGLE" ? "Google Ads" : "TikTok Ads";
              const isSyncing = syncingAds === platform.platformGroup;
              const run = platform.lastRun;
              const routing = platform.platformGroup === "META"
                ? "EN account ending 7711: Cairo/CairoCC → Cairo; Istanbul/IstanbulCC → Istanbul; unmatched → Unallocated. Other active DentSpa accounts → Istanbul."
                : platform.platformGroup === "GOOGLE"
                  ? "2901 → Istanbul (TRY) · 1994 → Istanbul (TRY) · 2445 → Cairo (TRY). Manager account 0784 is ignored."
                  : "All TikTok advertising is assigned to the Cairo call centre.";
              return <article className="connected-ad" key={platform.platformGroup}>
                <div className="connected-ad__identity"><span className={`status-dot ${platform.connected ? "" : "status-dot--neutral"}`} /><div><strong>{name}</strong><span>{platform.connected ? "Connected through Composio" : "Connection required"}</span></div></div>
                <div className="connected-ad__metric"><span>Last update</span><strong>{run?.finished_at ?? "Not synced yet"}</strong><small>{run ? `${run.date_from} – ${run.date_to}` : composioData?.automaticSchedule ?? "Daily"}</small></div>
                <div className="connected-ad__metric"><span>Latest import</span><strong>{(run?.row_count ?? 0).toLocaleString()} rows</strong><small>{run ? `${run.account_count} account${run.account_count === 1 ? "" : "s"} · ${run.status.toLowerCase()}` : "Ready for first sync"}</small></div>
                <button type="button" className="primary-button connected-ad__sync" disabled={Boolean(syncingAds) || !platform.connected || !syncMonth} onClick={() => void refreshAdvertising(platform.platformGroup)}><RefreshCw size={14} />{isSyncing ? `Syncing ${syncMonth}…` : `Sync ${name}`}</button>
                <p className="connected-ad__routing">{routing}</p>
                {platform.platformGroup === "GOOGLE" && <label className="connected-ad__routing">Google account to sync <select aria-label="Google account to sync" value={googleSyncAccount} disabled={Boolean(syncingAds)} onChange={event => setGoogleSyncAccount(event.target.value)}><option value="">All mapped Google accounts</option>{sourceData.find(source => source.id === "google")?.accounts.filter(account => account.active).map(account => <option key={account.id} value={account.id}>{account.name} · {account.external_id}</option>)}</select></label>}
                {run?.status === "FAILED" && <p className="connected-ad__error"><AlertCircle size={13} />{run.error_message ?? "The last synchronization failed."}</p>}
              </article>;
            })}
            {!composioData?.platforms?.length && <div className="connected-ad connected-ad--empty"><AlertCircle size={16} /><span>Advertising connection status is not available yet.</span></div>}
          </div>
        </section>

        <details ref={fallbackImports} className={`report-surface admin-grid__full fallback-imports ${focusUploads ? "upload-target" : ""}`}>
          <summary><div><h2>CSV fallback imports</h2><p>Hidden fallback for historical corrections or a platform connection problem.</p></div><span>Open imports</span></summary>
          <section ref={uploadSection} className="fallback-imports__content">
          <div className="surface-header"><div><h2>Manual file imports</h2><p>Use only when API synchronization is unavailable. Duplicate files remain protected.</p></div></div>
          <div className="source-list">
            {sources.map((source) => {
              const platforms = sourceData.filter((platform) => source.platformIds.includes(platform.id));
              const activeAccounts = platforms.flatMap((platform) => platform.accounts).filter((account) => account.active);
              const configuredCentres = platforms.flatMap((platform) => platform.centres);
              const centres = [...new Set([...activeAccounts.map((account) => account.default_centre).filter(Boolean), ...configuredCentres])].join(", ");
              return <div className="source-row" key={source.name}>
              <div className="source-logo"><FileSpreadsheet size={18} /></div>
              <div className="source-row__name"><strong>{source.name}</strong><span>{activeAccounts.length ? `${activeAccounts.length} active account${activeAccounts.length === 1 ? "" : "s"}` : "Account identified from CSV"}</span></div>
              <div className="source-row__centre"><span>Allocation</span><strong>{centres || "Configured in code"}</strong></div>
              <div className="source-row__status"><span className="status-dot status-dot--neutral" /><div><strong>CSV fallback</strong><span>{source.state}</span></div></div>
              <button
                type="button"
                className="secondary-button secondary-button--small"
                disabled={importingPlatformFile}
                onClick={() => openPlatformFilePicker(source.group)}
              ><Upload size={14} />{importingPlatformFile && selectedPlatformGroup === source.group ? "Reading…" : "Upload CSV"}</button>
            </div>;})}
          </div>
          </section>
        </details>

        {status && <div className="admin-status admin-status--grid"><AlertCircle size={15} />{status}</div>}

        <section className="report-surface admin-grid__full">
          <div className="surface-header"><div><h2>Google account access</h2><p>Only approved Gmail addresses can sign in.</p></div><button type="button" className="secondary-button secondary-button--small" onClick={() => setAccessOpen(true)}><Plus size={14} />Add person</button></div>
          <table className="access-table">
            <thead><tr><th>Person</th><th>Google account</th><th>Role</th><th>Status</th><th>Last active</th><th /></tr></thead>
            <tbody>
              {userData.map((user) => <tr key={user.id}><td><span className="person-cell"><span className={`avatar ${user.role === "ADMINISTRATOR" ? "avatar--violet" : ""}`}>{user.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</span><strong>{user.name}</strong></span></td><td>{user.email}</td><td><span className={`role-pill ${user.role === "VIEWER" ? "role-pill--viewer" : ""}`}>{user.role === "ADMINISTRATOR" ? "Administrator" : "Viewer"}</span></td><td><span className="active-label"><span className="status-dot" />{user.active ? "Active" : "Inactive"}</span></td><td>{user.last_login_at ?? "Not yet"}</td><td><button className="icon-button icon-button--quiet" type="button" aria-label={`Change access level for ${user.name}`} onClick={() => setRoleUser(user)}><MoreHorizontal size={17} /></button></td></tr>)}
            </tbody>
          </table>
        </section>
        <McpConnectionRequests onChange={onConnectionsChanged} />
      </div>
      {accessOpen && <AccessDrawer onClose={() => setAccessOpen(false)} onSaved={loadAdmin} />}
      {roleUser && <RoleDrawer user={roleUser} onClose={() => setRoleUser(null)} onSaved={loadAdmin} />}
      {salesPreview && <div className="drawer-layer" role="presentation">
        <button className="drawer-backdrop" type="button" aria-label="Close sales import preview" onClick={() => setSalesPreview(null)} />
        <aside className="drawer" aria-label="Sales import preview">
          <div className="drawer__header">
            <div><p className="eyebrow">Zoho Analytics · Ads Sold Rate Report Linked by Payments</p><h2>Sales import preview</h2><p>{salesPreview.paymentsFile} + {salesPreview.leadSourcesFile}</p></div>
            <button type="button" className="icon-button" onClick={() => setSalesPreview(null)} aria-label="Close"><X size={19} /></button>
          </div>
          <div className="drawer__body">
            <div className="sales-preview-summary">
              <div><span>Payments</span><strong>{salesPreview.paymentRows.toLocaleString()}</strong><small>{salesPreview.distinctPatients.toLocaleString()} paying patients</small></div>
              <div><span>Revenue</span><strong>{money.format(salesPreview.totalRevenueUsdCents / 100)}</strong><small>USD, as exported</small></div>
              <div><span>Attributed</span><strong>{Math.round(salesPreview.attributedRows / salesPreview.paymentRows * 100)}%</strong><small>{salesPreview.unattributedRows.toLocaleString()} unattributed</small></div>
              <div><span>Lead cohorts</span><strong>{salesPreview.cohortMonths.first} – {salesPreview.cohortMonths.last}</strong><small>By lead-created month</small></div>
            </div>
            <table className="access-table sales-preview-table">
              <thead><tr><th>Platform</th><th>Payments</th><th>Revenue</th></tr></thead>
              <tbody>
                {salesPreview.platformTotals.map((row) => <tr key={row.label}><td><strong>{row.label}</strong></td><td>{row.payments.toLocaleString()}</td><td>{money.format(row.revenueUsdCents / 100)}</td></tr>)}
              </tbody>
            </table>
            <p>Published snapshot: {salesPreview.previousPaymentRows.toLocaleString()} payments · {money.format(salesPreview.previousRevenueUsdCents / 100)}. New snapshot: {salesPreview.paymentRows.toLocaleString()} payments · {money.format(salesPreview.totalRevenueUsdCents / 100)} net revenue.</p>
            {salesPreview.removedScopes.length > 0 && <details><summary>{salesPreview.removedScopes.length} groups will be removed</summary><ul>{salesPreview.removedScopes.map((scope) => <li key={scope}>{scope}</li>)}</ul></details>}
            {salesPreview.warnings.length > 0 && <div className="sales-preview-warnings">{salesPreview.warnings.map((warning) => <p key={warning}><AlertCircle size={13} />{warning}</p>)}</div>}
            <label><input type="checkbox" checked={confirmFullSnapshot} onChange={(event) => setConfirmFullSnapshot(event.target.checked)} /> I confirm both files contain full historical data for all channels and call centres, not filtered exports. I have reviewed the replacement totals above.</label>
            <div className="login-security"><ShieldCheck size={17} /><p>Importing replaces the previous CRM snapshot entirely. Report months covered by this snapshot switch to these numbers; the audit trail records the import.</p></div>
          </div>
          <div className="drawer__footer">
            <button type="button" className="secondary-button" onClick={() => setSalesPreview(null)}>Cancel</button>
            <button type="button" className="primary-button" disabled={salesBusy !== null || !confirmFullSnapshot} onClick={() => void commitSales()}><Check size={16} />{salesBusy === "import" ? "Importing…" : "Publish sales snapshot"}</button>
          </div>
        </aside>
      </div>}
      {platformImportPreview && <PlatformImportDrawer
        preview={platformImportPreview.preview}
        duplicate={platformImportPreview.duplicate}
        onClose={() => setPlatformImportPreview(null)}
        onImported={(preview) => {
          setPlatformImportPreview({ preview, duplicate: false });
          setStatus(`${preview.platformGroup} actual spend is now included in Planning, the report, and the master table.`);
          void loadAdmin();
        }}
      />}
    </>
  );
}

const masterValue = (entry: MasterEntry) => money.format((entry.amountUsdCents ?? 0) / 100);

const masterNativeValue = (entry: MasterEntry) => {
  if (!entry.currency || entry.nativeAmountMinor === null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: entry.currency, maximumFractionDigits: 2 }).format(entry.nativeAmountMinor / 100);
};

function FxEditDrawer({ month, rates, onClose, onSaved }: {
  month: string;
  rates: FxRate[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [tryRate, setTryRate] = useState(String(rates.find((rate) => rate.quote_currency === "TRY")?.native_per_usd ?? ""));
  const [gbpRate, setGbpRate] = useState(String(rates.find((rate) => rate.quote_currency === "GBP")?.native_per_usd ?? ""));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateFx(month, Number(tryRate), Number(gbpRate));
      await onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The monthly averages could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  return <div className="drawer-layer" role="presentation">
    <button className="drawer-backdrop" type="button" aria-label="Close exchange-rate editor" onClick={onClose} />
    <aside className="drawer drawer--compact" aria-label={`Edit exchange rates for ${month}`}>
      <div className="drawer__header"><div><p className="eyebrow">Monthly average</p><h2>Edit {month} rates</h2><p>Native currency received for one US dollar.</p></div><button type="button" className="icon-button" onClick={onClose}><X size={19} /></button></div>
      <div className="drawer__body">
        <label className="field-label">USD / TRY average<input className="field-control" type="number" step="0.0001" min="0" value={tryRate} onChange={(event) => setTryRate(event.target.value)} /></label>
        <label className="field-label">USD / GBP average<input className="field-control" type="number" step="0.000001" min="0" value={gbpRate} onChange={(event) => setGbpRate(event.target.value)} /></label>
        <div className="login-security"><LockKeyhole size={17} /><p>A manual correction is recorded in the audit trail and will not be overwritten by the automatic daily refresh.</p></div>
        {error && <div className="form-error"><AlertCircle size={15} />{error}</div>}
      </div>
      <div className="drawer__footer"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button type="button" className="primary-button" disabled={saving || !Number(tryRate) || !Number(gbpRate)} onClick={() => void save()}><Check size={16} />{saving ? "Saving…" : "Save averages"}</button></div>
    </aside>
  </div>;
}

function DeleteEntryDialog({ entry, onClose, onDeleted }: {
  entry: MasterEntry;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteMasterEntry(entry.id);
      await onDeleted();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The entry could not be deleted.");
    } finally {
      setDeleting(false);
    }
  };
  return <div className="dialog-layer" role="presentation"><button type="button" className="drawer-backdrop" aria-label="Cancel deletion" onClick={onClose} /><section className="confirm-dialog" role="dialog" aria-modal="true" aria-label="Delete master data entry"><span className="confirm-dialog__icon"><Trash2 size={20} /></span><h2>Delete this {entry.entryType.toLowerCase()} entry?</h2><p><strong>{entry.month} · {entry.platform} · {entry.centre}</strong></p><p>This removes the entry from reporting and records the deletion in the audit trail.</p>{error && <div className="form-error"><AlertCircle size={15} />{error}</div>}<div><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button type="button" className="danger-button" disabled={deleting} onClick={() => void remove()}><Trash2 size={15} />{deleting ? "Deleting…" : "Delete entry"}</button></div></section></div>;
}

function MasterDataView() {
  const [year, setYear] = useState("2026");
  const [entries, setEntries] = useState<MasterEntry[]>([]);
  const [counts, setCounts] = useState<Record<MasterEntryType, number>>({ PLAN: 0, SPEND: 0 });
  const [rates, setRates] = useState<FxRate[]>([]);
  const [entryType, setEntryType] = useState<"ALL" | MasterEntryType>("ALL");
  const [status, setStatus] = useState<string | null>(null);
  const [syncingRates, setSyncingRates] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState<MasterEntry | null>(null);
  const [editFxMonth, setEditFxMonth] = useState<string | null>(null);

  const loadMaster = useCallback(async () => {
    const [master, rates2024, rates2025, rates2026] = await Promise.all([
      api.masterData(year), api.fx("2024"), api.fx("2025"), api.fx("2026"),
    ]);
    setEntries(master.entries);
    setCounts(master.counts);
    setRates([...rates2024.rates, ...rates2025.rates, ...rates2026.rates]);
  }, [year]);
  useEffect(() => { void loadMaster().catch((error: unknown) => setStatus(error instanceof Error ? error.message : "Master data could not be loaded.")); }, [loadMaster]);

  const [showZeroEntries, setShowZeroEntries] = useState(false);
  const [openEntryMonths, setOpenEntryMonths] = useState<Set<string> | null>(null);
  const typedEntries = entryType === "ALL" ? entries : entries.filter((entry) => entry.entryType === entryType);
  const zeroEntries = typedEntries.filter((entry) => !entry.amountUsdCents).length;
  const visibleEntries = showZeroEntries ? typedEntries : typedEntries.filter((entry) => entry.amountUsdCents);
  const entryMonths = useMemo(() => {
    const byMonth = new Map<string, MasterEntry[]>();
    for (const entry of visibleEntries) byMonth.set(entry.month, [...(byMonth.get(entry.month) ?? []), entry]);
    return [...byMonth.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([month, rows]) => ({
      month,
      rows,
      spend: rows.filter((row) => row.entryType === "SPEND").reduce((sum, row) => sum + (row.amountUsdCents ?? 0), 0),
      plan: rows.filter((row) => row.entryType === "PLAN").reduce((sum, row) => sum + (row.amountUsdCents ?? 0), 0),
    }));
  }, [visibleEntries]);
  // The current month (else the latest with actual spend) starts open.
  const defaultOpenMonth = entryMonths.find((group) => group.month === currentMonthKey)?.month
    ?? entryMonths.find((group) => group.spend !== 0)?.month ?? entryMonths[0]?.month;
  const openMonths = openEntryMonths ?? new Set(defaultOpenMonth ? [defaultOpenMonth] : []);
  const toggleEntryMonth = (month: string) => setOpenEntryMonths(() => {
    const next = new Set(openMonths);
    if (next.has(month)) next.delete(month); else next.add(month);
    return next;
  });
  const fxMonths = useMemo(() => {
    const result: Array<{ month: string; rates: FxRate[] }> = [];
    const [toYear, toMonth] = currentMonthKey.split("-").map(Number);
    for (let cursor = 2024 * 12; cursor <= toYear * 12 + toMonth - 1; cursor += 1) {
      const month = `${Math.floor(cursor / 12)}-${String(cursor % 12 + 1).padStart(2, "0")}`;
      result.push({ month, rates: rates.filter((rate) => rate.month === month) });
    }
    return result.reverse();
  }, [rates]);

  const syncRateHistory = async () => {
    setSyncingRates(true);
    setStatus(null);
    try {
      const result = await api.syncFxHistory("2025-01", currentMonthKey);
      await loadMaster();
      setStatus(`Monthly averages are complete through ${result.data.to}. ${result.data.synced.length} month${result.data.synced.length === 1 ? " was" : "s were"} refreshed.`);
    } catch (caught) {
      setStatus(caught instanceof Error ? caught.message : "Exchange-rate history could not be synchronized.");
    } finally {
      setSyncingRates(false);
    }
  };

  return <>
    <div className="page-heading"><div><p className="eyebrow">Database control</p><h1>Master data</h1><p>Review every reporting entry and monthly currency average.</p></div><div className="page-heading__actions"><label className="select-control"><CalendarDays size={15} /><select value={year} onChange={(event) => setYear(event.target.value)}><option>2026</option><option>2025</option><option>2024</option></select></label></div></div>
    {status && <div className="master-status"><AlertCircle size={15} />{status}</div>}

    <section className="master-summary"><div><span>Plans</span><strong>{counts.PLAN.toLocaleString()}</strong></div><div><span>Actual spend</span><strong>{counts.SPEND.toLocaleString()}</strong></div></section>

    <section className="report-surface master-section">
      <div className="surface-header"><div><h2>Monthly exchange-rate averages</h2><p>USD/TRY and USD/GBP daily averages from January 2025 through the current month.</p></div><button type="button" className="secondary-button" disabled={syncingRates} onClick={() => void syncRateHistory()}><RefreshCw size={15} />{syncingRates ? "Refreshing history…" : "Refresh averages"}</button></div>
      <div className="fx-history-wrap"><table className="access-table fx-history-table"><thead><tr><th>Month</th><th>USD / TRY average</th><th>USD / GBP average</th><th>Coverage</th><th>Source</th><th /></tr></thead><tbody>{fxMonths.map(({ month, rates: monthRates }) => { const tryRate = monthRates.find((rate) => rate.quote_currency === "TRY"); const gbpRate = monthRates.find((rate) => rate.quote_currency === "GBP"); const observations = Math.max(tryRate?.observation_count ?? 0, gbpRate?.observation_count ?? 0); const manual = monthRates.some((rate) => rate.source === "MANUAL_OVERRIDE"); return <tr key={month} className={month === currentMonthKey ? "fx-row--current" : undefined}><td><strong>{month}</strong>{month === currentMonthKey && <small className="master-reference">Month to date</small>}</td><td><strong>{tryRate ? tryRate.native_per_usd.toFixed(4) : "Missing"}</strong></td><td><strong>{gbpRate ? gbpRate.native_per_usd.toFixed(6) : "Missing"}</strong></td><td>{observations ? `${observations} daily observations` : "Not synchronized"}</td><td><span className={manual ? "manual-pill" : "frozen-pill"}>{manual ? "Manual override" : month === currentMonthKey ? "Automatic MTD" : "Automatic average"}</span></td><td><button type="button" className="icon-button icon-button--quiet" aria-label={`Edit ${month} exchange rates`} onClick={() => setEditFxMonth(month)}><Pencil size={15} /></button></td></tr>;})}</tbody></table></div>
      <div className="table-footer"><p><span className="status-dot" />A new month is created automatically by the daily Cloudflare schedule. Completed months remain frozen unless an administrator edits them.</p></div>
    </section>

    <section className="report-surface master-section">
      <div className="surface-header"><div><h2>Reporting entries</h2><p>The normalized records currently used by planning and performance reports.</p></div><div className="surface-tools"><label className="select-control"><Database size={15} /><select value={entryType} onChange={(event) => setEntryType(event.target.value as typeof entryType)}><option value="ALL">All entries</option><option value="PLAN">Plans</option><option value="SPEND">Actual spend</option></select></label><span className="table-count">{visibleEntries.length.toLocaleString()} rows</span>{zeroEntries > 0 && <button type="button" className="text-button" onClick={() => setShowZeroEntries((shown) => !shown)}>{showZeroEntries ? "Hide $0 rows" : `Show ${zeroEntries} $0 row${zeroEntries === 1 ? "" : "s"}`}</button>}</div></div>
      <div className="master-table-wrap"><table className="access-table master-table"><thead><tr><th>Entry</th><th>Platform / account</th><th>Call centre</th><th>Reporting value</th><th>Native value</th><th>Data source</th><th>Updated</th><th /></tr></thead><tbody>{entryMonths.length === 0 ? <tr><td colSpan={8}><span className="muted-table-note">No entries match this view.</span></td></tr> : entryMonths.map((group) => {
        const open = openMonths.has(group.month);
        return <Fragment key={group.month}>
          <tr className="master-month-row"><td colSpan={8}><button type="button" className="master-month-toggle" aria-expanded={open} onClick={() => toggleEntryMonth(group.month)}>
            <ChevronDown size={15} style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }} />
            <strong>{new Date(`${group.month}-01T00:00:00Z`).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" })}</strong>
            <span>Actual {money.format(group.spend / 100)}{group.plan ? ` · Plan ${money.format(group.plan / 100)}` : ""} · {group.rows.length} {group.rows.length === 1 ? "entry" : "entries"}</span>
          </button></td></tr>
          {open && group.rows.map((entry) => <tr key={entry.id}><td><span className={`entry-pill entry-pill--${entry.entryType.toLowerCase()}`}>{entry.entryType === "SPEND" ? "Actual" : "Plan"}</span></td><td><span className="platform-name"><PlatformLogo platform={entry.platform} /><strong>{entry.platform}</strong></span>{(entry.accountName || entry.sourceReference) && <small className="master-reference" title={entry.sourceReference ?? undefined}>{entry.accountName ?? entry.sourceReference}</small>}</td><td><CentreBadge centre={entry.centre} /></td><td><strong>{masterValue(entry)}</strong></td><td>{masterNativeValue(entry)}</td><td><span className="master-source">{entry.locked && <LockKeyhole size={12} />}{entry.source}</span></td><td>{entry.updatedAt.slice(0, 16).replace("T", " ")}</td><td><button type="button" className="icon-button icon-button--quiet icon-button--danger" aria-label={`Delete ${entry.entryType.toLowerCase()} entry`} onClick={() => setDeleteEntry(entry)}><Trash2 size={15} /></button></td></tr>)}
        </Fragment>;
      })}</tbody></table></div>
      <div className="table-footer"><p><span className="status-dot" />Every deletion and manual currency correction is retained in the audit trail.</p></div>
    </section>
    {deleteEntry && <DeleteEntryDialog entry={deleteEntry} onClose={() => setDeleteEntry(null)} onDeleted={loadMaster} />}
    {editFxMonth && <FxEditDrawer month={editFxMonth} rates={rates.filter((rate) => rate.month === editFxMonth)} onClose={() => setEditFxMonth(null)} onSaved={loadMaster} />}
  </>;
}

type SessionUser = { id: string; email: string; name: string; role: "ADMINISTRATOR" | "VIEWER" };

function FacebookAdRows({ nodes, depth, parentKey, expanded, onToggle }: {
  nodes: FacebookAdNode[];
  depth: number;
  parentKey: string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
}) {
  return <>
    {nodes.map((node) => {
      const key = `${parentKey}/${node.id}`;
      const roas = node.spendComplete && node.spendUsdCents > 0 ? node.revenueUsdCents / node.spendUsdCents : null;
      const open = expanded.has(key);
      return <Fragment key={key}>
        <tr className={depth === 0 ? "fb-row fb-row--campaign" : "fb-row"}>
          <td style={{ paddingLeft: `${12 + depth * 22}px` }}>
            {node.children?.length
              ? <button type="button" className="fb-row__toggle" aria-expanded={open} onClick={() => onToggle(key)}><ChevronDown size={13} style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }} />{node.name}</button>
              : <span className="fb-row__leaf">{node.name}</span>}
          </td>
          <td className="detail-cell--numeric">{node.spendComplete ? money.format(node.spendUsdCents / 100) : '—'}</td>
          <td className="detail-cell--numeric">{node.clicks.toLocaleString()}</td>
          <td className="detail-cell--numeric"><strong>{money.format(node.revenueUsdCents / 100)}</strong></td>
          <td className="detail-cell--numeric">{node.sales.toLocaleString()}</td>
          <td className="detail-cell--numeric"><strong className={roas === null ? "" : roas >= 1 ? "value-positive" : "value-low"}>{roas === null ? "—" : `${roas.toFixed(2)}×`}</strong></td>
        </tr>
        {open && node.children && <FacebookAdRows nodes={node.children} depth={depth + 1} parentKey={key} expanded={expanded} onToggle={onToggle} />}
      </Fragment>;
    })}
  </>;
}

function FacebookAdsView({ year, centre }: { year: string; centre: string }) {
  const [data, setData] = useState<FacebookAdsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    let active = true;
    setData(null);
    api.facebookAds(year, centre)
      .then((result) => { if (active) { setData(result); setError(null); } })
      .catch((caught: unknown) => { if (active) setError(caught instanceof Error ? caught.message : "Facebook ad data could not load."); });
    return () => { active = false; };
  }, [year, centre]);
  const toggle = (key: string) => setExpanded((current) => {
    const next = new Set(current);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  if (error) return <div className="preview-notice"><span><AlertCircle size={16} /></span><div><strong>Facebook ad data could not load</strong><p>{error}</p></div></div>;
  if (!data) return <div className="empty-detail"><Database size={18} />Loading Facebook ad performance…</div>;
  return (
    <section className="report-surface">
      <div className="surface-header">
        <div><h2>Facebook ads return</h2><p>Spend from the Meta sync joined by name to CRM revenue, cohorted by lead-created month.</p></div>
        {data.matchRate !== null && <div className="sales-import-last"><span>Revenue matched to synced ads</span><strong>{Math.round(data.matchRate * 100)}%</strong><small>{money.format(data.unmatchedRevenueUsdCents / 100)} unmatched</small></div>}
      </div>
      {data.warnings.map((warning) => <div className="admin-status" key={warning}><AlertCircle size={15} />{warning}</div>)}
      <div className="detail-table-wrap">
        <table className="detail-table fb-ads-table">
          <thead><tr><th>Campaign / ad set / ad</th><th className="detail-cell--numeric">Spend</th><th className="detail-cell--numeric">Clicks</th><th className="detail-cell--numeric">Revenue</th><th className="detail-cell--numeric">Sales</th><th className="detail-cell--numeric">ROAS</th></tr></thead>
          <tbody>
            {data.campaigns.length === 0
              ? <tr><td colSpan={6}><span className="muted-table-note">No Facebook-attributed payments or synced spend for this period.</span></td></tr>
              : <FacebookAdRows nodes={data.campaigns} depth={0} parentKey="" expanded={expanded} onToggle={toggle} />}
          </tbody>
        </table>
      </div>
      <div className="table-footer"><p><span className="status-dot" />Recent cohorts always look weaker: their revenue is still maturing while spend is already complete.</p></div>
    </section>
  );
}

function GoogleSearchTermsView({ year, centre }: { year: string; centre: string }) {
  const [data, setData] = useState<GoogleSearchTermsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState("All months");
  const [search, setSearch] = useState("");
  useEffect(() => {
    let active = true;
    setData(null);
    setMonth("All months");
    api.googleSearchTerms(year, centre)
      .then((result) => { if (active) { setData(result); setError(null); } })
      .catch((caught: unknown) => { if (active) setError(caught instanceof Error ? caught.message : "Google search terms could not load."); });
    return () => { active = false; };
  }, [year, centre]);
  const months = useMemo(() => [...new Set((data?.terms ?? []).map((term) => term.month))].sort().reverse(), [data]);
  const visible = useMemo(() => (data?.terms ?? [])
    .filter((term) => month === "All months" || term.month === month)
    .filter((term) => !search || `${term.searchTerm} ${term.campaign} ${term.adGroup}`.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 300), [data, month, search]);
  if (error) return <div className="preview-notice"><span><AlertCircle size={16} /></span><div><strong>Google search terms could not load</strong><p>{error}</p></div></div>;
  if (!data) return <div className="empty-detail"><Database size={18} />Loading Google search terms…</div>;
  return (
    <section className="report-surface">
      <div className="surface-header">
        <div><h2>Google search terms</h2><p>What people searched before clicking a Google ad. Spend-side only — the CRM does not record keywords, so revenue cannot be joined.</p></div>
        <div className="surface-tools">
          <label className="search-control"><Search size={15} /><input type="search" placeholder="Search term or campaign" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          <label className="select-control"><CalendarDays size={15} /><select aria-label="Search-term month" value={month} onChange={(event) => setMonth(event.target.value)}><option>All months</option>{months.map((option) => <option key={option}>{option}</option>)}</select></label>
        </div>
      </div>
      {data.warnings.map((warning) => <div className="admin-status" key={warning}><AlertCircle size={15} />{warning}</div>)}
      <div className="detail-table-wrap">
        <table className="detail-table">
          <thead><tr><th>Search term</th><th>Month</th><th>Campaign · ad group</th><th className="detail-cell--numeric">Impressions</th><th className="detail-cell--numeric">Clicks</th><th className="detail-cell--numeric">Spend</th></tr></thead>
          <tbody>
            {visible.length === 0
              ? <tr><td colSpan={6}><span className="muted-table-note">{data.totalTerms === 0 ? "No search-term data has been synced yet. It arrives with the next Google Ads synchronization." : "No search terms match this filter."}</span></td></tr>
              : visible.map((term) => <tr key={`${term.month}:${term.campaign}:${term.adGroup}:${term.searchTerm}`}>
                  <td><strong>{term.searchTerm}</strong></td>
                  <td>{term.month}</td>
                  <td><span className="master-reference">{term.campaign}{term.adGroup ? ` · ${term.adGroup}` : ""}</span></td>
                  <td className="detail-cell--numeric">{term.impressions.toLocaleString()}</td>
                  <td className="detail-cell--numeric">{term.clicks.toLocaleString()}</td>
                  <td className="detail-cell--numeric"><strong>{money.format(term.spendUsdCents / 100)}</strong></td>
                </tr>)}
          </tbody>
        </table>
      </div>
      <div className="table-footer"><p><span className="status-dot" />{data.totalTerms.toLocaleString()} terms · {money.format(data.totalSpendUsdCents / 100)} total spend · {data.totalClicks.toLocaleString()} clicks in {year}.</p></div>
    </section>
  );
}

type ExploreTab = "facebook" | "google";

function ExploreView() {
  const [tab, setTab] = useState<ExploreTab>("facebook");
  const [year, setYear] = useState("2026");
  const [centre, setCentre] = useState("All call centres");
  const tabs: Array<{ id: ExploreTab; label: string; hint: string }> = [
    { id: "facebook", label: "Facebook ads", hint: "Campaign, ad set and ad ROAS from synced spend and CRM revenue" },
    { id: "google", label: "Google search terms", hint: "Where Google budget goes, term by term" },
  ];
  return (
    <>
      <div className="page-heading">
        <div><p className="eyebrow">Underlying data</p><h1>Explore</h1><p>The detail behind the report, one level down. The main report stays simple; this is where you dig.</p></div>
        <div className="page-heading__actions">
          <label className="select-control"><CalendarDays size={15} /><select aria-label="Explore year" value={year} onChange={(event) => setYear(event.target.value)}><option>2026</option><option>2025</option></select></label>
          <label className="select-control"><select aria-label="Explore call centre" value={centre} onChange={(event) => setCentre(event.target.value)}><option>All call centres</option><option>Istanbul</option><option>Cairo</option><option>Unallocated</option></select></label>
        </div>
      </div>
      <div className="explore-tabs" role="tablist">
        {tabs.map((item) => <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} className={`explore-tab ${tab === item.id ? "explore-tab--active" : ""}`} onClick={() => setTab(item.id)}><strong>{item.label}</strong><small>{item.hint}</small></button>)}
      </div>
      {tab === "facebook" && <FacebookAdsView year={year} centre={centre} />}
      {tab === "google" && <GoogleSearchTermsView year={year} centre={centre} />}
    </>
  );
}

const LEAD_PLATFORM_LABELS: Record<string, string> = {
  meta_en: "Meta EN", meta_other: "Meta Other", meta_unallocated: "Meta — account unspecified", google: "Google",
  tiktok: "TikTok", microsoft: "Microsoft", x: "X Ads", pinterest: "Pinterest",
};
const LEAD_PLATFORM_ORDER = Object.keys(LEAD_PLATFORM_LABELS);
const CPL_CHANNELS: Array<{ id: ChannelSummary["id"]; label: string }> = [
  { id: "meta", label: "Meta combined" }, { id: "google", label: "Google combined" }, { id: "tiktok", label: "TikTok" },
  { id: "microsoft", label: "Microsoft" }, { id: "x", label: "X Ads" }, { id: "pinterest", label: "Pinterest" },
];
const CPL_PRIMARY: ChannelSummary["id"][] = ["meta", "google", "tiktok", "microsoft"];
const channelOfPlatform = (platformId: string): ChannelSummary["id"] | null => {
  if (platformId.startsWith("meta")) return "meta";
  return CPL_CHANNELS.some((channel) => channel.id === platformId) ? platformId as ChannelSummary["id"] : null;
};
const platformOrder = (platformId: string) => { const index = LEAD_PLATFORM_ORDER.indexOf(platformId); return index < 0 ? 99 : index; };

type CplFigures = { spend: number; leads: number; istanbulLeads: number; cairoLeads: number; spendComplete: boolean };
type CplLine = CplFigures & { monthKey: string; monthLabel: string; platformId: string; label: string };
type CplRow = CplFigures & { key: string; label: string; logo?: string; note?: string };
type CplGroup = CplRow & { children: CplRow[] };

const emptyFigures = (): CplFigures => ({ spend: 0, leads: 0, istanbulLeads: 0, cairoLeads: 0, spendComplete: true });
const addFigures = <T extends CplFigures>(target: T, source: CplFigures): T => {
  target.spend += source.spend;
  target.leads += source.leads;
  target.istanbulLeads += source.istanbulLeads;
  target.cairoLeads += source.cairoLeads;
  if (!source.spendComplete) target.spendComplete = false;
  return target;
};
const cplText = (spend: number, leads: number) => (leads > 0 && spend > 0 ? money2.format(spend / leads) : "—");
const UNSPECIFIED_META_NOTE = "Facebook leads with no ad account in Zoho have no spend of their own; they count in Meta's combined CPL.";

function CplChannelCard({ label, id, figures, onOpen }: { label: string; id: ChannelSummary["id"]; figures: CplFigures; onOpen: () => void }) {
  const cpl = cplText(figures.spend, figures.leads);
  return (
    <article className="channel-metric-card">
      <div className="channel-metric-card__top">
        <span className="channel-metric-card__label"><i className={`platform-mark platform-mark--${id}`} />{label}</span>
        <span>YTD</span>
      </div>
      <div className="channel-metric-card__roas" title={figures.spendComplete ? undefined : "Some of this channel's spend is still being verified, so this CPL may change."}>
        {cpl === "—" ? <strong>—</strong> : <button type="button" className="channel-metric-card__proof" title="Open this platform month by month" onClick={onOpen}><strong>{cpl}</strong></button>}
        <span>{figures.spendComplete ? "CPL" : "Provisional CPL"}</span>
      </div>
      <div className="channel-metric-card__values">
        <div title={money.format(figures.spend)}><span>Invested</span><strong>{compactMoney.format(figures.spend)}</strong></div>
        <div><span>Leads</span><strong>{figures.leads.toLocaleString()}</strong></div>
      </div>
    </article>
  );
}

function CplView() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [centre, setCentre] = useState<"All call centres" | Centre>("All call centres");
  const [groupBy, setGroupBy] = useState<"month" | "platform">("month");
  const [months, setMonths] = useState<MonthRow[]>([]);
  const [leads, setLeads] = useState<LeadCounts | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const [showAllChannels, setShowAllChannels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const table = useRef<HTMLElement>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setMonths([]); setLeads(null);
    Promise.all([api.report(year, centre), api.leadCounts(year)])
      .then(([report, leadCounts]) => { if (!cancelled) { setMonths(report.months); setLeads(leadCounts.data); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "Could not load CPL"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, centre]);

  // One line per month and platform, joining report spend with lead counts.
  const lines = useMemo<CplLine[]>(() => {
    const byKey = new Map<string, CplLine>();
    const line = (monthKey: string, monthLabel: string, platformId: string, label?: string) => {
      const key = `${monthKey}|${platformId}`;
      let found = byKey.get(key);
      if (!found) {
        found = { ...emptyFigures(), monthKey, monthLabel, platformId, label: label ?? LEAD_PLATFORM_LABELS[platformId] ?? platformId };
        byKey.set(key, found);
      }
      return found;
    };
    for (const report of months) {
      for (const detail of report.details) {
        const target = line(report.monthKey ?? report.month, report.month, detail.platformId ?? detail.platform, detail.platform);
        target.spend += detail.spend;
        if (detail.spendComplete === false) target.spendComplete = false;
      }
    }
    for (const count of leads?.rows ?? []) {
      if (centre !== "All call centres" && count.centre !== centre) continue;
      const [y, m] = count.month.split("-").map(Number);
      const target = line(count.month, new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }), count.platformId);
      target.leads += count.leads;
      if (count.centre === "Istanbul") target.istanbulLeads += count.leads;
      if (count.centre === "Cairo") target.cairoLeads += count.leads;
    }
    return [...byKey.values()].filter((item) => item.spend !== 0 || item.leads > 0);
  }, [months, leads, centre]);

  const channels = useMemo(() => CPL_CHANNELS.map((channel) => ({
    ...channel,
    figures: lines.filter((item) => channelOfPlatform(item.platformId) === channel.id).reduce(addFigures, emptyFigures()),
  })), [lines]);
  const totals = lines.reduce(addFigures, emptyFigures());

  const groups = useMemo<CplGroup[]>(() => {
    if (groupBy === "month") {
      const byMonth = new Map<string, CplGroup>();
      for (const item of [...lines].sort((a, b) => a.monthKey.localeCompare(b.monthKey) || platformOrder(a.platformId) - platformOrder(b.platformId) || b.spend - a.spend)) {
        const group = byMonth.get(item.monthKey) ?? { ...emptyFigures(), key: item.monthKey, label: item.monthLabel, children: [] };
        addFigures(group, item);
        group.children.push({ ...item, key: item.platformId, logo: item.label, note: item.platformId === "meta_unallocated" ? UNSPECIFIED_META_NOTE : undefined });
        byMonth.set(item.monthKey, group);
      }
      return [...byMonth.values()];
    }
    return CPL_CHANNELS.map((channel) => {
      const byMonth = new Map<string, CplRow>();
      for (const item of lines.filter((entry) => channelOfPlatform(entry.platformId) === channel.id)) {
        const row = byMonth.get(item.monthKey) ?? { ...emptyFigures(), key: item.monthKey, label: item.monthLabel };
        byMonth.set(item.monthKey, addFigures(row, item));
      }
      const children = [...byMonth.values()].sort((a, b) => a.key.localeCompare(b.key));
      return { ...children.reduce(addFigures, emptyFigures()), key: channel.id, label: channel.label, logo: channel.label, children };
    }).filter((group) => group.children.length > 0);
  }, [lines, groupBy]);

  const toggle = (key: string) => setOpenGroups((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const switchGrouping = (next: "month" | "platform") => { setGroupBy(next); setOpenGroups(new Set()); };
  const openChannel = (id: string) => {
    setGroupBy("platform");
    setOpenGroups(new Set([id]));
    requestAnimationFrame(() => table.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const leadSplit = (row: CplFigures) =>
    centre === "All call centres" && (row.istanbulLeads || row.cairoLeads) ? <small className="split-line">IST {row.istanbulLeads.toLocaleString()} · CAI {row.cairoLeads.toLocaleString()}</small> : null;
  const figureCells = (row: CplRow, strong: boolean) => {
    const Value = strong ? "strong" : "span";
    return <>
      <td><Value>{row.spend ? money.format(row.spend) : "—"}</Value>{!row.spendComplete && <small className="metric-subline">Spend incomplete</small>}</td>
      <td><Value>{row.leads ? row.leads.toLocaleString() : "—"}</Value>{leadSplit(row)}</td>
      <td title={row.note}><Value>{cplText(row.spend, row.leads)}</Value></td>
    </>;
  };
  const visibleChannels = channels.filter((channel) => showAllChannels || CPL_PRIMARY.includes(channel.id));
  const hiddenCount = channels.length - CPL_PRIMARY.length;

  return <>
    <div className="page-heading"><div><p className="eyebrow">Report</p><h1>Cost per lead</h1><p>Spend ÷ new leads, by the month the lead came in.</p></div><div className="page-heading__actions">
      <label className="select-control">Year<select aria-label="CPL year" value={year} onChange={(event) => setYear(event.target.value)}>{[2026, 2025, 2024].map((value) => <option key={value}>{value}</option>)}</select></label>
      <label className="select-control"><select aria-label="CPL call centre" value={centre} onChange={(event) => setCentre(event.target.value as typeof centre)}>{["All call centres", "Istanbul", "Cairo", "Unallocated"].map((value) => <option key={value}>{value}</option>)}</select></label>
    </div></div>
    {error && <p role="alert">{error}</p>}
    {loading ? <p role="status">Loading CPL…</p> : !error && <>
      <div className="cpl-summary">
        <div><span>Total invested {year}</span><strong>{money.format(totals.spend)}</strong></div>
        <div><span>Leads generated</span><strong>{totals.leads.toLocaleString()}</strong></div>
        <div><span>Average CPL</span><strong>{cplText(totals.spend, totals.leads)}</strong></div>
      </div>

      <section className="channel-metric-section" aria-labelledby="cpl-channels-title">
        <div className="channel-metric-section__heading">
          <h2 id="cpl-channels-title">Cost per lead by platform</h2>
          <p>Year to date · {centre}</p>
        </div>
        <div className={`channel-metric-grid ${showAllChannels ? "channel-metric-grid--cols3" : ""}`}>
          {visibleChannels.map((channel) => <CplChannelCard key={channel.id} id={channel.id} label={channel.label} figures={channel.figures} onOpen={() => openChannel(channel.id)} />)}
        </div>
        {hiddenCount > 0 && <button type="button" className="text-button channel-metric-section__toggle" aria-expanded={showAllChannels} onClick={() => setShowAllChannels((open) => !open)}>
          {showAllChannels ? <>Hide other platforms <ChevronUp size={14} /></> : <>Show {hiddenCount} more platform{hiddenCount === 1 ? "" : "s"} <ChevronDown size={14} /></>}
        </button>}
      </section>

      <section className="surface cpl-breakdown" ref={table}>
        <div className="cpl-breakdown__heading">
          <h2>{groupBy === "month" ? "Month by month" : "Platform by platform"}</h2>
          <div className="cpl-view-toggle" role="group" aria-label="Group CPL by">
            <button type="button" aria-pressed={groupBy === "month"} onClick={() => switchGrouping("month")}>By month</button>
            <button type="button" aria-pressed={groupBy === "platform"} onClick={() => switchGrouping("platform")}>By platform</button>
          </div>
        </div>
        <div className="monthly-table-wrap">
          <table className="monthly-table cpl-table">
            <thead><tr><th className="month-column">{groupBy === "month" ? "Month" : "Platform"}</th><th>Invested</th><th>Leads</th><th>CPL</th></tr></thead>
            <tbody>
              {groups.map((group) => {
                const open = openGroups.has(group.key);
                return <Fragment key={group.key}>
                  <tr className={`month-row ${open ? "month-row--open" : ""}`}>
                    <td><button type="button" className="month-toggle" onClick={() => toggle(group.key)} aria-expanded={open}><ChevronDown size={17} />
                      {group.logo && <PlatformLogo platform={group.logo} />}
                      <span><strong>{group.label}</strong><small>{open ? (groupBy === "month" ? "Hide platforms" : "Hide months") : (groupBy === "month" ? "Show platforms" : "Show months")}</small></span></button></td>
                    {figureCells(group, true)}
                  </tr>
                  {open && group.children.map((row) => <tr key={`${group.key}-${row.key}`} className="platform-detail-row">
                    <td className="month-column"><div className="platform-detail-row__name"><span className="platform-name">{row.logo && <PlatformLogo platform={row.logo} />}{row.label}</span></div></td>
                    {figureCells(row, false)}
                  </tr>)}
                </Fragment>;
              })}
            </tbody>
          </table>
          {!groups.length && <p className="empty-detail">No spend or leads for this selection.</p>}
        </div>
      </section>
      <p className="preview-notice preview-notice--footnote cpl-footnote">
        {leads?.importedAt
          ? <>Leads are new Zoho contacts by the month they were created, attributed by lead source and ad account like sales. Call centre comes from the contact owner (Flypie owners are Cairo). Updated {shortDate(leads.importedAt) ?? leads.importedAt} from {leads.fileName}.</>
          : <>No lead counts yet. They're filled in when the Zoho sales exports are imported under Sources & access.</>}
      </p>
    </>}
  </>;
}

function McpConnectionGuide({ onManageAccess }: { onManageAccess: () => void }) {
  const url = "https://ads.dentspa.com/mcp";
  const [copyStatus, setCopyStatus] = useState("");
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopyStatus("Connection link copied."); }
    catch { setCopyStatus("Could not copy automatically. Select and copy the link above."); }
  };
  return <>
    <div className="page-heading"><div><p className="eyebrow">Settings</p><h1>MCP connection guide</h1><p>Connect your AI app to DentSpa reporting data, or reconnect an existing connection.</p></div></div>
    <section className="report-surface mcp-guide">
      <h2>Connection details</h2>
      <label htmlFor="mcp-server-url">Server URL</label>
      <div className="mcp-guide__url"><input id="mcp-server-url" readOnly value={url} onFocus={(event) => event.target.select()} /><button type="button" className="primary-button" onClick={() => void copy()}>Copy link</button></div>
      <p role="status">{copyStatus}</p>
      <p><strong>Transport:</strong> Streamable HTTP · <strong>Authentication:</strong> OAuth · <strong>Access:</strong> Read-only</p>
      <h2>Connect or reconnect</h2>
      <ol>
        <li>Open the connections or MCP settings in your AI app. For an existing DentSpa connection, choose its reconnect or sign-in option. Otherwise, add a remote MCP server.</li>
        <li>Use the server URL above. Select OAuth if the app asks for an authentication method. No API key is required.</li>
        <li>Sign in with the same Google account you use for this dashboard. It must be active on the DentSpa access list.</li>
        <li>Check the destination shown on the consent page before allowing access. Return to your AI app once authorization finishes.</li>
        <li>Test the connection by asking: “When was each DentSpa data source last updated?”</li>
      </ol>
      <h2>If you see “Waiting for approval”</h2>
      <p>A viewer's first connection from a new app needs administrator approval in Sources &amp; access → AI connection requests. Keep the approval page open; it continues automatically after approval. Requests expire after 30 minutes. Administrators' own connections and previously approved reconnections do not need this step.</p>
      <button type="button" className="secondary-button" onClick={onManageAccess}>Open Sources &amp; access</button>
      <h2>What the connection can read</h2>
      <p>Performance reports, spend and revenue proof, Meta advertising detail, Google search terms, exchange rates, and source freshness. It cannot change figures, plans, or advertising campaigns.</p>
      <p><strong>Privacy:</strong> Requested reporting data, including payment-level records with pseudonymous patient IDs, is sent to the AI provider you connect. Only approve a destination you trust.</p>
      <h2>Troubleshooting</h2>
      <ul><li>An expired session usually needs reconnection, not a new API key.</li><li>If access is denied, confirm the Google account and ask an administrator to check your dashboard access.</li><li>The URL belongs in your AI app's MCP settings; opening it as an ordinary webpage is not a connection test.</li></ul>
    </section>
  </>;
}

function AppShell({ user }: { user: SessionUser }) {
  const [view, setView] = useState<View>("report");
  const [uploadFocusKey, setUploadFocusKey] = useState(0);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const adminMenu = useRef<HTMLDivElement>(null);
  const isAdministrator = user.role === "ADMINISTRATOR";
  const [pendingConnections, setPendingConnections] = useState(0);
  const refreshPendingConnections = useCallback(() => {
    if (!isAdministrator) return;
    void api.mcpConnectionRequests().then((result) => setPendingConnections(result.requests.filter((request) => request.status === "PENDING").length)).catch(() => undefined);
  }, [isAdministrator]);
  useEffect(() => {
    refreshPendingConnections();
    const timer = window.setInterval(refreshPendingConnections, 60_000);
    return () => window.clearInterval(timer);
  }, [refreshPendingConnections]);
  const nav: { id: View; label: string }[] = isAdministrator
    ? [{ id: "report", label: "ROAS" }, { id: "cpl", label: "CPL" }, { id: "plan", label: "Planning" }]
    : [{ id: "report", label: "ROAS" }, { id: "cpl", label: "CPL" }];

  useEffect(() => {
    if (!adminMenuOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!adminMenu.current?.contains(event.target as Node)) setAdminMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAdminMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [adminMenuOpen]);

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    window.location.assign("/login");
  };
  const initials = user.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  const openUploads = () => {
    setAdminMenuOpen(false);
    setUploadFocusKey((current) => current + 1);
    setView("data");
  };
  const openAdminView = (nextView: "master" | "data" | "mcp") => {
    setAdminMenuOpen(false);
    setView(nextView);
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <Brand />
        <nav className="main-nav" aria-label="Primary navigation">
          {nav.map((item) => <button type="button" key={item.id} className={view === item.id ? "active" : ""} aria-current={view === item.id ? "page" : undefined} onClick={() => { setAdminMenuOpen(false); setView(item.id); }}>{item.label}</button>)}
        </nav>
        <div className="header-actions">
          <div className="sync-status"><span className="status-dot" /><span><strong>System online</strong><small>Data dates shown in reports</small></span></div>
          {isAdministrator && <div className="admin-menu" ref={adminMenu}>
            <button type="button" className={`icon-button admin-menu__trigger ${view === "master" || view === "data" ? "admin-menu__trigger--active" : ""}`} aria-label="Open administration menu" aria-haspopup="menu" aria-expanded={adminMenuOpen} onClick={() => setAdminMenuOpen((open) => !open)}><Settings size={18} />{pendingConnections > 0 && <span className="admin-menu__badge" aria-label={`${pendingConnections} AI connection request${pendingConnections === 1 ? "" : "s"} waiting`}>{pendingConnections}</span>}</button>
            {adminMenuOpen && <div className="admin-menu__dropdown" role="menu" aria-label="Administration">
              <div className="admin-menu__heading"><span>Administration</span><small>Data controls and access</small></div>
              <button type="button" role="menuitem" className={view === "mcp" ? "active" : ""} onClick={() => openAdminView("mcp")}><span><ShieldCheck size={17} /></span><div><strong>MCP connection guide</strong><small>Connection link, sign-in, and troubleshooting</small></div>{view === "mcp" && <Check size={15} />}</button>
              <button type="button" role="menuitem" className={view === "master" ? "active" : ""} onClick={() => openAdminView("master")}><span><Database size={17} /></span><div><strong>Master data</strong><small>Entries, exchange rates, and source review</small></div>{view === "master" && <Check size={15} />}</button>
              <button type="button" role="menuitem" className={view === "data" ? "active" : ""} onClick={() => openAdminView("data")}><span><FileSpreadsheet size={17} /></span><div><strong>Sources & access</strong><small>{pendingConnections > 0 ? `${pendingConnections} AI connection request${pendingConnections === 1 ? "" : "s"} waiting` : "CSV uploads and Google permissions"}</small></div>{view === "data" && <Check size={15} />}</button>
            </div>}
          </div>}
          <div className="user-identity" aria-label={`Signed in as ${user.name}`}><span className="avatar avatar--violet">{initials}</span><span><strong>{user.name.split(" ")[0]}</strong><small>{user.role === "ADMINISTRATOR" ? "Administrator" : "Viewer"}</small></span></div>
          <button type="button" className="icon-button logout-button" title="Sign out" aria-label="Sign out of DentSpa" onClick={() => void signOut()}><LogOut size={16} /></button>
        </div>
      </header>
      <main className="page-container">
        {view === "report" && <ReportView canEdit={isAdministrator} onManageUploads={openUploads} onViewMaster={() => setView("master")} />}
        {view === "cpl" && <CplView />}
        {isAdministrator && view === "plan" && <PlanView />}
        {isAdministrator && view === "master" && <MasterDataView />}
        {isAdministrator && view === "data" && <DataView onOpenMaster={() => setView("master")} focusUploads={uploadFocusKey} onConnectionsChanged={refreshPendingConnections} />}
        {isAdministrator && view === "mcp" && <McpConnectionGuide onManageAccess={() => setView("data")} />}
      </main>
      <footer className="app-footer"><Brand /><p>Production · Connected to Cloudflare D1</p><button className="text-button" type="button" onClick={() => void signOut()}><LogOut size={14} />Sign out</button></footer>
    </div>
  );
}

export default function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checkingSession, setCheckingSession] = useState(path !== "/login");

  useEffect(() => {
    const updatePath = () => setPath(window.location.pathname);
    window.addEventListener("popstate", updatePath);
    return () => window.removeEventListener("popstate", updatePath);
  }, []);

  useEffect(() => {
    if (path === "/login") {
      setCheckingSession(false);
      return;
    }
    setCheckingSession(true);
    void api.session().then((session) => {
      if (!session.authenticated || !session.user) {
        window.history.replaceState({}, "", "/login");
        setPath("/login");
        setUser(null);
      } else {
        setUser(session.user);
      }
    }).catch(() => {
      window.history.replaceState({}, "", "/login");
      setPath("/login");
      setUser(null);
    }).finally(() => setCheckingSession(false));
  }, [path]);

  if (path === "/login") return <LoginScreen />;
  if (checkingSession || !user) return <div className="boot-screen"><Brand /><span>Loading secure workspace…</span></div>;
  return <AppShell user={user} />;
}
